import {
  type FileFacts,
  type HandoverBlocker,
  type State,
  REVIEW_HANDOVERS,
  type Language,
  type ReviewHandover,
  countsOf,
  blockerForHandover,
  fileRequirementsOf,
  fieldVisible,
  criterionIdsIn,
  handbacksIn,
  evaluate,
  laagOf,
  ownLaag,
  ownsLaag,
  limitsOf,
  type RowFindings,
  rowsOf,
  requiredFields,
  requiredChecks,
  fieldCheckPasses,
  isEmpty,
  singleValueOf,
} from '@eformulieren/engine'
import {
  type Moment,
  type Person,
  type Projection,
  type Field,
  type FieldId,
  type VerwijzingOutcome,
  type ViewMode,
  newId,
} from '@eformulieren/model'
import { type ReactNode, type SyntheticEvent, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import type { CriterionOutcome, Text } from '@eformulieren/model'
import * as Y from 'yjs'
import { type PresenceSource, PresenceSummary, isPersonKey, participantNames } from '../collab/PresenceBar.js'
import { FieldPresence } from '../collab/FieldPresence.js'
import {
  outstandingCount,
  useThreadsPerSection,
} from '../comments/useThreads.js'
import { useSourcePicker } from '../citation/useSourcePicker.js'
import { useSectionCitations } from '../citation/useSectionCitations.js'
import { LayerReferenceList } from '../citation/LayerReferenceList.js'
import type { ScopeCitations } from '../citation/sectionCitations.js'
import { type CitationField, citationFieldsOf } from '../citation/collectClusters.js'
import {
  type Origin,
  beginThread,
} from '../document/sectionDocument.js'
import { emptyForPrefill, prefillCandidates, writePrefill } from '../document/prefill.js'
import { ownPresence, isNwoEmployee } from '../document/presence.js'
import { useLiveState } from '../document/useState.js'
import { FieldShell } from '../field/FieldShell.js'
import type { Shared, Issued, UploadOutcome } from '../field/controls/kinds.js'
import { ContextHeader, type HeaderProgress } from './ContextHeader.js'
import type { SeriesPosition } from './SeriesNavigator.js'
import { controlFor, labelViaHeader, takesInput } from '../field/registry.js'
import { Form, type FormProps, type SectionNavigation } from './Form.js'
import { type CriteriaState, PROGRESS, type SectionProgress, completeSections } from './TableOfContents.js'
import type { OutstandingEntry } from './OutstandingOverview.js'
import { inLanguage, localeOf } from '../language/text.js'
import { StorageStatus } from './StorageStatus.js'
import { LanguageSwitch } from './LanguageSwitch.js'
import { useFormPanels } from './formPanels.js'
import { HistoryBanner, type HistoryChoice, HistoryOverview, type VersionMoment, oneBeginning } from './HistoryPanel.js'
import type { FormPanel } from './RightPanel.js'
import { versieName } from './Form.js'
import { type State as HistoryState, showHistory, showLive, showsHistory, stateFromBytes } from '../collab/history.js'
import { authorsIn, colorFor } from '../collab/attribution.js'
import { stateOf as documentStateOf } from '../document/state.js'
import { ReviewButtons, reviewReason } from './ReviewButtons.js'
import { BusyButton } from './BusyButton.js'
import { readableBlockName } from '../field/controls/dossierValue.js'
import { historyPerQuestion, placedQuestions, questionLabels } from './history.js'
import { seriesRolesFor } from './seriesRoles.js'
import { WaitScreen } from './WaitScreen.js'
import { useStorage } from './useStorage.js'
import type { CommentRange } from '../document/commentRange.js'
import { type SharedEntry, SharedEntryBanner } from './SharedEntryBanner.js'
import { type CitationProps, bind } from './fieldBinding.js'

/**
 * Een vaste sleutel per `Y.Doc`, voor de React-sleutel van een veld (eisenboek 12.50). Een nummer en niet het
 * document zelf: een sleutel is een tekst.
 */
const documentIdentities = new WeakMap<Y.Doc, number>()
let nextDocumentIdentity = 1
function documentIdentity(doc: Y.Doc | undefined): string {
  if (doc === undefined) return 'geen'
  let known = documentIdentities.get(doc)
  if (known === undefined) {
    known = nextDocumentIdentity++
    documentIdentities.set(doc, known)
  }
  return String(known)
}

/**
 * Het complete invulbare formulier: een projectie plus sectiedocumenten erin, een
 * werkend formulier eruit.
 *
 * Dit is de plek waar alles bij elkaar komt, en met opzet een component van het
 * pakket en niet iets in de harnas: `apps/runtime` heeft in M3 precies dit nodig, en
 * de poort van M2 toetst het als geheel. Zou het in de harnas staan, dan was het
 * getoetste samenstel iets anders dan het uitgeleverde.
 *
 * Waar wat vandaan komt:
 *
 * - **de stand** uit de documenten (`useStand`), meelopend met elke wijziging, ook
 *   die van een medeschrijver;
 * - **de status** uit `evaluate` van de engine, één keer per wijziging en nergens
 *   anders vandaan (ontwerp 5.2);
 * - **de afleidingen** (verplicht, grenzen, bestandseisen, aantallen) uit de engine;
 * - **de identiteit** van de aanroeper, want die komt uit het token van de gastheer
 *   en nooit uit invoer van de client (R-154, ontwerp 6.9). In M2 geeft de aanroeper
 *   hem mee, vanaf M3 doet de pod dat.
 *
 * Indienen is hier de lokale variant: de knop gaat aan zodra er geen harde controle
 * meer openstaat (R-31) en geeft de stand terug. De transactie met revisie en
 * outbox-event is M3 (B9).
 *
 * **Welke handeling onderaan staat hangt af van de gedaante** (R-138a). In `toetsen` sluit de
 * toetser zijn beurt met een van de drie uitkomsten van R-181 (`ReviewButtons`), en staat
 * Indienen er niet: dat is de handeling van de eigenaar van de indiening (R-83, R-69) en sluit de
 * invoerlaag (`BEURTEN.ingediend`), die in een toetsbeurt niet loopt. In `nalezen` staat er
 * niets, want daar is het formulier alleen-lezen (R-86, R-133). In de andere gedaanten staat
 * Indienen. Tot de toetsknoppen stond Indienen in elke gedaante, ook in `toetsen` en `nalezen`,
 * en dan weigerde de pod hem.
 */

export interface FillFormProps {
  readonly projectie: Projection
  readonly mode: ViewMode
  readonly taal: Language
  readonly onLanguage?: (language: Language) => void
  /** Eén sectiedocument per sectie-id. Een bronsectie hoort er niet in (R-56). */
  readonly documenten: ReadonlyMap<string, Y.Doc>
  /**
   * De opgeloste verwijzingen, per veld-id (R-54, R-61, R-63).
   *
   * Komen van de pod, die ze tegen de dossierdocumenten van de instantie heeft opgelost; deze
   * schil rekent er niets aan uit, net zoals hij de werkverzameling niet zelf berekent. Optioneel
   * omdat een gastheer zonder verwijzingen ze niet hoeft mee te geven; ontbreekt de uitkomst voor
   * een veld dat er wel een vraagt, dan is de stand `onbeschikbaar` en niet leeg (R-61).
   */
  readonly verwijzingen?: Readonly<Record<string, VerwijzingOutcome>>
  /**
   * De uitkomst per criterium van deze beurt, per criterium-id (R-147, R-152).
   *
   * Dezelfde vorm en dezelfde reden als `verwijzingen`: ze komen van de pod en deze schil rekent
   * er niets aan uit. Wat erin staat is wat er al is vastgesteld: het oordeel als het is gegeven,
   * en wat een aangewezen controle heeft gevonden (R-180). Niet de vorige beurt; die staat in
   * `vorigeUitkomsten` en wordt naast het criterium getekend in plaats van erin.
   */
  readonly uitkomsten?: Readonly<Record<string, CriterionOutcome>>
  /**
   * Een oordeel vellen, of niets.
   *
   * **Ontbreekt hij, dan is een criterium alleen-lezen, en dat is geen halve toestand.** Een
   * oordeel gaat niet naar het sectiedocument maar als geheel naar een eigen route (U23, U21), en
   * een aanroeper die die route niet heeft kan het dus niet vastleggen. De control zegt dan dat
   * het oordeel er komt zodra het is gegeven, in plaats van een knop te tonen die niets doet. Het
   * voorbeeld van de configurator geeft deze prop wél mee, met een lokale, niet-opslaande route
   * (D-22, D-24): D-22 vraagt juist dat de toetser er hetzelfde control krijgt als in de praktijk.
   */
  readonly onVerdict?: (criterionId: string, verdict: Issued) => void | Promise<void>
  /**
   * De plek in de reeks, en de sprong ernaartoe (R-122).
   *
   * Optioneel: buiten een werkvoorraad is er geen reeks en hoort er geen navigator te staan. De
   * positie komt uit het aanmaakverzoek waarmee het processysteem de instantie aanmaakt, als
   * context meegeleverd net als de weergavemodus en de contextkop (ontwerp 3.4); geen postMessage
   * meer sinds besluit A van 7 september 2026 het inbeddingskanaal van B8 heeft laten vervallen.
   *
   * **`onNavigate` schrijft eerst weg.** Dat is geen belofte van deze schil maar van wie hem levert,
   * en het is de hele eis: wisselen zonder invoerverlies. Zolang die belofte loopt zet
   * `seriesBusy` de knoppen uit.
   */
  readonly series?: SeriesPosition
  readonly onNavigate?: (instantieId: string) => void
  readonly seriesBusy?: boolean
  /**
   * De deadline van de instantie, zichtbaar in het formulier zelf (R-128, R-78).
   *
   * Optioneel en niet fataal als hij ontbreekt: geen termijn gezet, of het ophalen mislukte, zijn
   * voor deze schil hetzelfde geval als "niets te tonen" (`apps/runtime/pagina/Formulierpagina.tsx`).
   */
  readonly deadline?: string
  /**
   * Dossier en uitvraagmoment, in de contextkop (R-125).
   *
   * Beide de ondoorzichtige verwijzing die de instantie al draagt (R-100), geen verzonnen
   * weergavenaam. Optioneel: de harnas-context en een dev-proef leveren ze vandaag niet.
   */
  readonly dossierId?: string
  readonly uitvraagRef?: string
  /**
   * De weergavenaam van het bouwblok van de publicatie, voor het label in de contextkop (R-125, ontwerp
   * 9.5). Optioneel om dezelfde reden als dossier en uitvraagmoment; zonder noemt de kop het een formulier.
   */
  readonly bloknaam?: Text
  /**
   * Het bouwblok van de publicatie, als slug (D-41): de modus met historie toont alleen de eerdere
   * antwoorden van het eigen blok (R-62, R-137). Het label van de contextkop komt uit `bloknaam`.
   */
  readonly blok?: string
  /** Wie er schrijft, uit het token van de gastheer (R-154). */
  readonly identiteit: Person
  /**
   * De instantie, voor het terugkeren op de plek van de laatste invoer (R-164, M8.7).
   *
   * Ontwerp 6.3 laat de plek van de bewaring open ("Ontwerpvoorstel: een klein
   * administratieveld in `apps/store`") en schrijft alleen voor wát er wordt bewaard: het
   * veld-id van de laatste invoer, per persoon en per instantie. Gekozen is voor
   * `localStorage`, in deze schil, zonder een pod- of storeronde: klein houden waar het kan
   * (werkregel 5), en de plek was niet vastgelegd. Ontbreekt deze prop (M2, een preview
   * zonder echte instantie), dan doet deze hele opdracht niets: er is dan geen sleutel om op
   * terug te vallen. De beperking die daarbij hoort staat in `docs/dekking.md`: dit werkt
   * per browser en niet per persoon op een ander apparaat, en gaat mee weg met site data.
   */
  readonly instantieId?: string
  /** De klok, injecteerbaar zodat een test hem vast kan zetten. */
  readonly nu: () => Moment
  readonly save: () => Promise<void>
  readonly onSubmit?: (state: State) => void | Promise<void>
  /**
   * Een toetsbeurt sluiten met een van de drie uitkomsten van R-181, of niets.
   *
   * Alleen in de gedaante `toetsen`. De schil schrijft eerst weg en vraagt bij goedkeuren en
   * afkeuren een bevestiging (7.8 voorstel 1); wat er daarna gebeurt is aan de aanroeper: de pod
   * sluit de beurt (`POST /overdrachten/:gebeurtenis`), het voorbeeld van de configurator doet
   * niets (D-24). Ontbreekt hij, dan staan de knoppen er wel maar doen ze niets, net als Indienen
   * zonder `onIndienen`.
   */
  readonly onHandover?: (event: ReviewHandover) => void | Promise<void>
  /** Of de documenten er zijn; tot dan het wachtscherm (valkuil 6). */
  readonly gereed?: boolean
  /**
   * Wat deze persoon bij deze overdracht moet bekijken, en wat erbij hoort (R-176, R-178a).
   *
   * Alles wat `GET /werkverzameling` van de pod levert, doorgegeven aan het skelet. Deze schil
   * rekent er niets aan uit en filtert er niets uit: de pod heeft de vorige stand en de
   * projectie, en dat is waar de begrenzing hoort (R-179).
   */
  readonly werkverzameling?: FormProps['werkverzameling']
  readonly comments?: FormProps['comments']
  readonly changedFields?: FormProps['changedFields']
  /**
   * Kandidaat-waarden uit de querystring, per veld-id (R-37, M8.6).
   *
   * Van de gastheer-schil, die `window.location.search` leest: deze schil raakt `location` zelf
   * nooit aan, dezelfde grens als bij `instantieId` (`leesContext` in `apps/runtime/pagina`). Een
   * waarde wordt alleen geschreven in een veld dat nog leeg is, en alleen voor `text`, `textarea`,
   * `radio` en `dropdown` (een keuzewaarde bovendien alleen als hij een bekende optie is): dat zijn de
   * controls waarvan de waarde een enkele tekst is, rechtstreeks vergelijkbaar met een
   * querystring-parameter. Een veld dat al een antwoord heeft wordt nooit overschreven, ook niet
   * bij een herlaadslag met dezelfde link.
   */
  readonly prefill?: Readonly<Record<string, string>>
  readonly previousOutcomes?: FormProps['previousOutcomes']
  /** De eerdere versies en de gekozen stand ernaast (R-96, R-174). */
  readonly earlierVersies?: FormProps['earlierVersies']
  readonly eerdereWaarden?: FormProps['eerdereWaarden']
  readonly chosenVersie?: FormProps['chosenVersie']
  readonly onEarlierVersie?: FormProps['onEarlierVersie']
  /**
   * De samenwerkverbinding per sectie, voor de presence en de cursor (R-72).
   *
   * Van de gastheer, want die opent de verbindingen; de documenten komen al zo binnen. Een
   * verbinding is hier met opzet een `unknown` met een awareness ernaast: de cursor-extensie
   * heeft de provider zelf nodig en de presence-balk leest de awareness, en deze schil hoeft
   * geen van beide te kennen.
   *
   * Zonder deze prop is er geen presence en geen cursor, en werkt het formulier gewoon.
   */
  readonly connections?: ReadonlyMap<string, SectionConnection>
  /**
   * Hoe een persoon in beeld heet, of niets.
   *
   * Van de gastheer: het formulier heeft geen gebruikerstabel (R-64), dus het kent alleen
   * tokensubjecten. Zonder deze functie staat het subject er, en dat is leesbaar genoeg om niet
   * te liegen.
   */
  readonly naamVan?: (person: string) => string
  /**
   * Een bestand van de invuller aanleveren bij de opslag, langs de pod (R-104, U25). Zonder deze functie,
   * zoals in de preview van de configurator, rekent de browser alleen de hash uit.
   */
  readonly uploadAttachment?: (fieldId: string, file: File) => Promise<UploadOutcome>
  /**
   * Wat de opslag weet van de bijlagen in de stand, per hash met soort en grootte (R-104). Met deze kaart
   * toetst `bestandseisen` hier hetzelfde als de indienpoort van de pod; zonder alleen naam en grootte.
   */
  readonly bestanden?: ReadonlyMap<string, FileFacts>
  /** Hashes in de stand waarvan de kaart nog niets weet, zodat de gastheer de feiten kan ophalen. */
  readonly onUnknownFiles?: (hashes: readonly string[]) => void
  /**
   * Wat de gastheer vooraan in de kopbalk zet: het merkblok en het kruimelpad (R-157).
   *
   * Eén compacte kopbalk en niet twee rijen (ontwerp van het invulscherm, 29 oktober 2026): merk,
   * kruimelpad, opslagstatus, wie er meewerken, de taal en delen staan op één regel. Merk en
   * kruimelpad horen bij de pagina van de pod en niet bij het formulier, en het voorbeeld in de
   * configurator heeft zijn eigen merkblok (`ProductBar.tsx`); daarom komen ze als slot binnen.
   */
  readonly barStart?: ReactNode
  /**
   * De titel van de pagina voor de browser en de schermlezer, telkens als hij verandert (WCAG 2.4.2,
   * audit van 30 oktober 2026): "<titel> · <blok> · NWO", met "Fout: " ervoor zolang het overzicht
   * van wat nog openstaat in beeld is en er iets openstaat. Een callback en geen `document.title`
   * hier, want het voorbeeld in de configurator heeft zijn eigen paginatitel.
   */
  readonly onDocumentTitle?: (title: string) => void
  /**
   * De sessie is verlopen (R-154, audit van 30 oktober 2026). De pagina toont daarvoor haar eigen
   * melding bovenaan; het formulier zegt dan in de kopbalk dat het niet verbonden is in plaats van
   * "Alles is opgeslagen" en "Alleen u", en zet Indienen en de toetsknoppen uit met die reden. Wat
   * er staat blijft staan, zodat het over te nemen is.
   */
  readonly sessieVerlopen?: boolean
  /**
   * De knop voor delen (R-64, R-65d, R-66), van de pagina. Sinds 2 november 2026 in de werkbalk van het
   * formulier naast Bronnen, Opmerkingen en Geschiedenis, en niet meer in de kopbalk (eisenboek 12.47):
   * delen gaat over dit formulier en niet over het product. Wie hem ziet en wat hij zegt beslist de
   * pagina (`ShareButton`); zonder deze prop staat er geen knop, zoals op de adviseurpagina en in het
   * voorbeeld van de configurator.
   */
  readonly delen?: ReactNode
  /**
   * De knop "Uitloggen" van de pagina, als laatste in de kopbalk (R-154, ontwerp 3.3). Een eigen slot,
   * zodat de knop "Meer" op een telefoon kan zeggen wat erachter staat.
   */
  readonly uitloggen?: ReactNode
  /**
   * De secties die nu verborgen zijn door een zichtbaarheidsregel (R-33), telkens als dat verandert.
   * De knop voor delen staat in `delen` en kent de antwoorden niet; de dialoog zet een verborgen
   * sectie onder "Alle secties tonen" (G-10, besluit 1 van 30 oktober 2026).
   */
  readonly onHiddenSections?: (sectionIds: readonly string[]) => void
  /**
   * Het label voor een stand die niets opslaat: het voorbeeld van de configurator (D-24).
   *
   * Het voorbeeld geeft een `save` mee die niets doet, en zonder deze prop meldt de schil dan toch
   * "Alles is opgeslagen" in de kopbalk en "Automatisch opgeslagen om ..." in de voet; in een
   * voorbeeld is dat geen ware mededeling (audit look and feel van 30 oktober 2026, V1). Met deze
   * prop staat het label op de plek van de opslagstatus, en noemt de voet geen opslagmoment en geen
   * automatisch opslaan. Zonder de prop verandert er niets.
   */
  readonly previewLabel?: ReactNode
  /**
   * De weergavenaam per bouwblok van buiten, voor de herkomst van een verwijzing (R-58).
   *
   * De naam van het eigen blok komt uit `bloknaam`; een blok van buiten kent de pod nog niet bij
   * naam (zie hieronder bij de verwijzing), en dan staat er de slug. De configurator kent de namen
   * wel, uit de registry in de opslag, en geeft ze in het voorbeeld mee zodat daar geen slug staat.
   */
  readonly bronNamen?: Readonly<Record<string, Text>>
  /**
   * Advies bij de vragen en sectiekoppen, en het paneel ernaast (R-184, ontwerp 7.4a). De pagina geeft
   * ze alleen aan de interne kant mee, en alleen als er advies is voor deze kijker (R-184b); zonder
   * deze props verandert er niets aan het formulier. Zie `Form`.
   */
  readonly advies?: FormProps['advies']
  readonly zijpaneel?: FormProps['zijpaneel']
  /**
   * Of de gedaante `nalezen` de melding "Ingediend op …" voor de aanvrager toont (standaard ja). De
   * adviseurpagina zet haar uit: die zin ("NWO controleert uw aanvraag") is niet aan de adviseur gericht.
   */
  readonly submittedNotice?: boolean
  /**
   * De kijker kwam alleen via een deling binnen (`mijnToegang: 'deling'` uit `GET /delingen`): een
   * medeschrijver of een meelezer (R-69, audit delen G-03 en G-16, mockup 07). Dan een banner met zijn
   * rechten, "lezen" of "schrijven" in de inhoudsopgave, en in de voet "Indienen doet de aanvrager"
   * met "Mijn secties controleren". Zonder deze prop is het formulier dat van de aanvrager.
   */
  readonly deling?: SharedEntry
  /**
   * Een opmerking plaatsen langs de pod, voor een sectie die de lezer alleen leest (`POST /opmerkingen`,
   * R-73, besluit 8 van 30 oktober 2026). Een meelezer schrijft niet in het sectiedocument: de pod neemt
   * dat niet aan, en het document in deze browser zou dan uiteenlopen met dat van de pod. Hij post, en het
   * draadje komt binnen met de update van de pod. Zonder deze prop geen opmerking bij een leessectie.
   */
  readonly onOpmerking?: (request: CommentRequest) => Promise<{ readonly draad: string }>
  /**
   * Een eigen opmerking verwijderen langs de pod, voor een sectie die de lezer alleen leest
   * (`DELETE /opmerkingen/:draad`, eisenboek 12.45). Om dezelfde reden als `onOpmerking`: een meelezer
   * schrijft niet in zijn sectiedocument. Zonder deze prop geen verwijderknop bij een leessectie.
   */
  readonly onOpmerkingVerwijderen?: (request: CommentDeleteRequest) => Promise<unknown>
  /**
   * Een akkoord per persoon geven of intrekken langs de pod (`POST /akkoorden`, eisenboek 12.56). De pod
   * schrijft het in het document, met persoon en e-mailadres uit het token; de browser schrijft het nooit
   * zelf. Een weigering komt terug als fout met de melding van de pod. Zonder deze prop toont het veld
   * alleen wie akkoord gaf.
   */
  readonly onAkkoord?: (request: { readonly sectie: string; readonly veld: string; readonly handeling: 'geven' | 'intrekken' }) => Promise<unknown>
  /**
   * Een melding boven het formulier dat de toegang veranderde terwijl het openstond (besluit 9, audit
   * delen G-02): "Uw toegang is gewijzigd" met Opnieuw laden, of een sectie die nu alleen te lezen is.
   * Van de pagina, die de sluiting en het bericht van de pod hoort.
   */
  readonly toegangMelding?: ReactNode
  /**
   * De standen die de pod in deze beurt bewaarde, met het begin van de beurt, voor het paneel
   * Geschiedenis (R-97, R-188, ontwerp 6.9, `HistoryPanel.tsx`). Zonder deze prop, zoals in het
   * voorbeeld van de configurator, staat Geschiedenis er alleen als er eerder ingediende versies zijn.
   */
  readonly versiemomenten?: readonly VersionMoment[]
  /** Het paneel Geschiedenis gaat open: de pagina haalt de standen opnieuw op. */
  readonly onVersiemomenten?: () => void
}

/**
 * Wat `onOpmerking` verstuurt: een nieuw draadje bij geselecteerde tekst in rich-text-veld `veldId`
 * (met `bereik`), of een reactie in een bestaand draadje (met `draad`).
 */
export interface CommentRequest {
  readonly sectie: string
  readonly veldId?: string
  /** Het bereik in het rich-text-veld, als twee relatieve posities van Yjs (`markCommentRange`). */
  readonly bereik?: CommentRange
  readonly draad?: string
  readonly tekst: string
}

/** Wat `onOpmerkingVerwijderen` verstuurt: een eigen bericht (`bericht`), of zonder het hele draadje. */
export interface CommentDeleteRequest {
  readonly sectie: string
  readonly draad: string
  readonly bericht?: string
}

/** De invulgedaanten: daar zegt een leessectie zelf dat ze alleen te lezen is (G-16). */
const FILL_MODES: ReadonlySet<ViewMode> = new Set(['invullen', 'aanvullen'])

/** Wat een sectieverbinding aan de schil biedt. Zie {@link FillFormProps.connections}. */
export interface SectionConnection {
  readonly provider: unknown
  readonly awareness?: PresenceSource
  /**
   * De voorvulling uit de querystring naar de pod sturen, die haar in zijn document zet (R-37).
   *
   * Zonder deze functie schrijft de schil zelf; zie de voorvulling in `FillForm`.
   */
  readonly prefill?: (values: Readonly<Record<string, string>>) => void
}

/** Het tijdstip van de laatste opslag, als "11:42" (f2, R-157). */
function timeOf(moment: Moment, language: Language): string {
  return new Date(moment).toLocaleTimeString(localeOf(language), {
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function FillForm({
  projectie: liveProjection,
  mode,
  taal: language,
  onLanguage,
  documenten: documents,
  verwijzingen,
  uitkomsten: outcomes,
  onVerdict,
  series,
  onNavigate,
  seriesBusy,
  deadline,
  dossierId,
  uitvraagRef,
  bloknaam: blockName,
  blok,
  identiteit: identity,
  instantieId,
  nu: now,
  save,
  onSubmit,
  onHandover,
  gereed: ready = true,
  werkverzameling,
  comments,
  changedFields,
  prefill,
  previousOutcomes,
  earlierVersies,
  eerdereWaarden: earlierValues,
  chosenVersie,
  onEarlierVersie,
  connections,
  naamVan: nameOf,
  uploadAttachment,
  bestanden: knownFiles,
  onUnknownFiles,
  barStart,
  onDocumentTitle,
  sessieVerlopen: sessionExpired = false,
  delen: shareButton,
  uitloggen: logout,
  onHiddenSections,
  previewLabel,
  bronNamen: sourceNames,
  advies: advice,
  zijpaneel: sidePanel,
  submittedNotice = true,
  deling: sharedEntry,
  onOpmerking: postComment,
  onOpmerkingVerwijderen: deleteComment,
  onAkkoord: onConsent,
  toegangMelding: accessNotice,
  versiemomenten: deliveredMoments,
  onVersiemomenten: onVersionMoments,
}: FillFormProps): ReactNode {
  const versionMoments = useMemo(() => (deliveredMoments === undefined ? undefined : oneBeginning(deliveredMoments)), [deliveredMoments])
  /**
   * Een stand uit de geschiedenis in beeld (paneel Geschiedenis): dan is alles alleen-lezen, net als na
   * een verlopen sessie, en zegt de voet waarom indienen niet kan. De projectie zelf verandert niet; wat
   * de lezer mag blijft wat de pod zegt, alleen schrijven staat zolang uit.
   */
  const [historyChoice, setHistoryChoice] = useState<HistoryChoice | undefined>(undefined)
  const chosenMoment = historyChoice === undefined ? undefined : versionMoments?.find((entry) => entry.id === historyChoice.id)
  const inHistory = chosenMoment !== undefined
  const projection = useMemo(() => (inHistory ? readOnlyProjection(liveProjection) : liveProjection), [inHistory, liveProjection])
  const locked = sessionExpired || inHistory
  const lockReason = sessionExpired ? SESSION_REASON[language] : HISTORY_REASON[language]
  const state = useLiveState(projection.secties, documents)
  // De draadjes van alle secties in één abonnement; zie de kop van `useThreadsPerSection`.
  const threadsPer = useThreadsPerSection(documents)
  // De verwijzingen doen mee aan de zichtbaarheid (eisenboek 12.31), met dezelfde waarden als de pod.
  const { status, zichtbaarheid: visibility, rijen: rows } = useMemo(
    () =>
      evaluate(projection.secties, state, {
        ...(knownFiles === undefined ? {} : { bestanden: knownFiles }),
        ...(verwijzingen === undefined ? {} : { verwijzingen }),
      }),
    [projection, state, knownFiles, verwijzingen],
  )
  // De hashes in de stand waar de kaart nog niets van weet: een bestand dat een medeschrijver net
  // aanleverde, of de stand bij het openen. De gastheer haalt de feiten op; tot dan faalt de regel
  // voor dat bestand, zoals bij de indienpoort (R-104).
  const unknownKey = useMemo(() => {
    if (knownFiles === undefined) return ''
    const hashes = new Set<string>()
    const collect = (answer: unknown): void => {
      if (answer === null || typeof answer !== 'object') return
      if ('documenten' in answer && Array.isArray(answer.documenten)) {
        for (const document of answer.documenten as { hash: string }[]) if (!knownFiles.has(document.hash)) hashes.add(document.hash)
      }
      if ('rijen' in answer && Array.isArray(answer.rijen)) {
        for (const row of answer.rijen as { waarden: Record<string, unknown> }[]) for (const cell of Object.values(row.waarden)) collect(cell)
      }
    }
    for (const answer of Object.values(state)) collect(answer)
    return [...hashes].sort().join(' ')
  }, [state, knownFiles])
  useEffect(() => {
    if (unknownKey !== '') onUnknownFiles?.(unknownKey.split(' '))
    // Alleen op de verzameling hashes; de functie wisselt per render van identiteit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unknownKey])
  /**
   * Hoe vaak de invuller vroeg wat er nog openstaat (R-24, R-140); nul is nog nooit.
   *
   * Vanaf de eerste keer staan alle meldingen aan (`toonAlleMeldingen`), ook bij velden die hij
   * nooit heeft aangeraakt, en blijft het overzicht staan. Dat wijkt bewust af van R-25 (een harde
   * melding komt bij het verlaten van het veld): wie expliciet vraagt om alles te zien wat indienen
   * tegenhoudt, hoort ook de melding te zien van het veld waar hij nu in typt, en een melding die
   * na zijn vraag nog wacht op het verlaten van het veld is precies wat hem liet vastlopen. Vóór de
   * vraag geldt R-25 ongewijzigd.
   */
  const [request, setRequest] = useState(0)
  const showAllNotices = request > 0
  /**
   * Hoe vaak de invuller het overzicht opende vanuit de voortgang in de kop, zonder te controleren
   * (besluit van 2 november 2026). Dat opent hetzelfde overzicht in de neutrale vorm: geen foutkleur,
   * geen "Fout:" in de paginatitel en geen meldingen bij velden die hij nog niet aanraakte (R-25). Pas
   * "Controleren en indienen" (`request`) maakt het een foutsamenvatting.
   */
  const [look, setLook] = useState(0)
  /**
   * De ene weg naar het overzicht naast "Controleren en indienen": de knop in de voortgang in de kop, en
   * in de toetsgedaante dezelfde knop in de voet (besluit van 2 november 2026). Neutraal zolang de
   * invuller niet controleerde; daarna de foutvorm, voor beide gelijk (`fout: request > 0`). De focus
   * gaat naar het overzicht en een schermlezer leest zijn kop (`OutstandingOverview`, R-140).
   */
  /**
   * Of het overzicht open staat, los van `look` en `request` (live test van 3 november 2026): die twee
   * tellen hoe vaak erom gevraagd is, voor de focus en de foutvorm, en gaan nooit terug. De knop in de
   * kop en in de voet van de toetsgedaante is nu een schakelaar ("Toon wat er openstaat", "Verberg wat
   * er openstaat"); daarvoor bracht een tweede klik de invuller opnieuw naar het overzicht, en ging het
   * nooit meer dicht. Controleren opent het en zet de focus op de kop, ook als het al open stond.
   */
  const [overviewOpen, setOverviewOpen] = useState(false)
  const overviewId = useId()
  /** Wat het overzicht opende, om de focus na Sluiten of Escape daar terug te zetten (WCAG 2.4.3). */
  const overviewOpener = useRef<HTMLElement | null>(null)
  const rememberOpener = useCallback((event?: SyntheticEvent<HTMLElement>) => {
    // De knop uit het event en niet alleen `activeElement`: Safari geeft een knop bij een klik geen focus.
    const active = event?.currentTarget ?? document.activeElement
    overviewOpener.current = active instanceof HTMLElement && active !== document.body ? active : null
  }, [])
  const openOverview = useCallback(
    (event?: SyntheticEvent<HTMLElement>) => {
      rememberOpener(event)
      setOverviewOpen(true)
    },
    [rememberOpener],
  )
  const showOutstanding = useCallback(
    (event?: SyntheticEvent<HTMLElement>) => {
      if (overviewOpen) {
        // Dichtdoen met de schakelaar zelf: de focus staat daar al.
        setOverviewOpen(false)
        return
      }
      openOverview(event)
      setLook((previous) => previous + 1)
    },
    [overviewOpen, openOverview],
  )
  const closeOverview = useCallback(() => {
    // Synchroon, zodat de focus pas verschuift als het overzicht weg is.
    flushSync(() => setOverviewOpen(false))
    const opener = overviewOpener.current
    // Is wat het opende er niet meer, dan de schakelaar, die bij dit overzicht hoort.
    const target = opener?.isConnected === true ? opener : document.querySelector(`[aria-controls="${CSS.escape(overviewId)}"]`)
    if (target instanceof HTMLElement) target.focus()
  }, [overviewId])
  /**
   * De basis voor de ankers waar het overzicht naartoe linkt.
   *
   * Eén `useId()` per formulier, zodat een anker uniek is op de pagina, ook als de configurator
   * meer dan één formulier tekent; daarachter het veld-id, en bij een cel ook het rij-id, want
   * dezelfde kolom staat in elke rij.
   */
  const anchorBase = useId()
  const submitReasonId = useId()
  const reviewSentenceId = useId()
  const anchorOf = useCallback(
    (fieldId: string, rowId?: string): string =>
      rowId === undefined ? `${anchorBase}-veld-${fieldId}` : `${anchorBase}-veld-${fieldId}-${rowId}`,
    [anchorBase],
  )
  const required = useMemo(() => requiredFields(projection.secties), [projection])
  /**
   * De akkoordvelden die deze lezer niet zelf geeft (besluit van 3 november 2026, R-69, R-89): in een
   * laag die hij niet bezit (`ownsLaag`), dus voor een medeschrijver in de invoerlaag. Het akkoord is
   * daar niet zijn werk: hij ziet het alleen-lezen met "Dit akkoord geeft de indiener", zonder
   * sterretje en zonder melding, en het telt niet mee in zijn voortgang en in wat er voor hem openstaat.
   * De pod zet een wijziging van hem terug (`apps/runtime/src/consentGuard.ts`). De evaluatie zelf
   * verandert niet: de poort `akkoordJa` rekent in browser en pod hetzelfde (ontwerp 5.9).
   */
  const foreignConsents = useMemo(() => {
    const out = new Set<string>()
    for (const section of projection.secties) {
      const laag = laagOf(section.type)
      // Alleen waar hij verder wel schrijft: wie de sectie alleen leest (een toetser in de invoerlaag,
      // nalezen) ziet het akkoord als elk antwoord dat hij niet kan bewerken (R-133).
      if (laag === null || ownsLaag(projection, laag) || section.recht !== 'bewerken') continue
      for (const field of section.velden) {
        if (field.control === 'toggle' && field.akkoord === true) out.add(field.id)
        if (field.control === 'herhaalgroep') {
          for (const column of field.rijVelden) if (column.control === 'toggle' && column.akkoord === true) out.add(column.id)
        }
      }
    }
    return out
  }, [projection])
  const requiredRules = useMemo(() => requiredChecks(projection.secties), [projection])
  const limits = useMemo(() => limitsOf(projection.secties), [projection])
  const requirements = useMemo(() => fileRequirementsOf(projection.secties), [projection])
  const counts = useMemo(() => countsOf(projection.secties), [projection])
  /**
   * De rich-text-veld-id's per sectie, voor een vast component dat over de hele sectie leest
   * (R-50): de citatie-engine achter `Referentielijst` moet elke citatie in de sectie zien en
   * niet alleen die van zijn eigen veld-id.
   *
   * Vooraf berekend en niet inline in `renderField`: een `Map` per sectie houdt de array-referentie
   * per sectie stabiel tussen renders, en `gebruikCitaties` herabonneert bij een nieuwe referentie
   * (zie zijn eigen docstring).
   */
  const richtextFieldIdsPerSection = useMemo(() => {
    const card = new Map<string, readonly CitationField[]>()
    for (const section of projection.secties) {
      card.set(
        section.id,
        citationFieldsOf(section.velden),
      )
    }
    return card
  }, [projection])
  /**
   * Eén citatieberekening per laag, over de secties in de projectie van deze lezer (R-50, eisenboek
   * 12.9): de labels in elk rich-text-veld en de literatuurlijst komen uit dezelfde uitkomst, naar
   * CitatenPOC's `ResultsContext`. Alleen wat in de projectie staat telt mee; zie
   * `useSectionCitations` voor waarom.
   */
  const citationScopes = useMemo(() => {
    const perLaag = new Map<string, { sectieId: string; fieldIds: readonly CitationField[] }[]>()
    for (const section of projection.secties) {
      const laag = laagOf(section.type)
      if (laag === null || laag === undefined) continue
      const list = perLaag.get(laag) ?? []
      list.push({ sectieId: section.id, fieldIds: richtextFieldIdsPerSection.get(section.id) ?? [] })
      perLaag.set(laag, list)
    }
    return [...perLaag.values()]
  }, [projection, richtextFieldIdsPerSection])
  const citationsPer = useSectionCitations(documents, citationScopes)
  /**
   * De literatuurlijst aan het eind van een laag zonder geplaatste referentielijst (opdracht van 30
   * oktober 2026, R-46, R-50, eisenboek 12.9). Staat het component ergens in de laag, dan is dat de
   * lijst en komt er geen tweede. Dezelfde uitkomst als de labels in de tekst, dus dezelfde nummering.
   */
  const layerLists = useMemo(() => {
    const lists: { laag: 'invoer' | 'toets'; node: ReactNode }[] = []
    for (const layer of ['invoer', 'toets'] as const) {
      const members = projection.secties.filter((section) => laagOf(section.type) === layer)
      const placed = members.some((section) => section.velden.some((field) => field.control === 'component' && 'component' in field && field.component === 'referentielijst'))
      const first = members[0]
      if (placed || first === undefined) continue
      const entries = citationsPer.get(first.id)?.literatuurlijst ?? []
      if (entries.length === 0) continue
      lists.push({ laag: layer, node: <LayerReferenceList laag={layer} taal={language} literatuurlijst={entries} /> })
    }
    return lists
  }, [projection, citationsPer, language])
  // De bronnenkiezer biedt de bibliotheek van de hele laag in de projectie (R-50), zie `useSourcePicker`.
  const scopeOfSection = useMemo(() => {
    const card = new Map<string, readonly string[]>()
    for (const scope of citationScopes) {
      const ids = scope.map(({ sectieId }) => sectieId)
      for (const id of ids) card.set(id, ids)
    }
    return (sectionId: string): readonly string[] => card.get(sectionId) ?? [sectionId]
  }, [citationScopes])
  // Beheren in de kiezer (opdracht van 30 oktober 2026): welke bronnen in de tekst van de laag staan,
  // uit dezelfde berekening als de literatuurlijst, en in welke secties deze lezer kan schrijven.
  const sourcesInUse = useCallback(
    (sectionId: string): ReadonlySet<string> => {
      const result = citationsPer.get(sectionId)
      const used = new Set<string>()
      if (result !== undefined && 'perSection' in result) {
        for (const part of (result as ScopeCitations).perSection.values()) for (const id of part.sources) used.add(id)
      }
      return used
    },
    [citationsPer],
  )
  const canWriteSection = useCallback(
    (sectionId: string): boolean => projection.secties.find((section) => section.id === sectionId)?.recht === 'bewerken',
    [projection],
  )
  const sourcePicker = useSourcePicker(documents, language, () => newId('bron'), scopeOfSection, {
    inUse: sourcesInUse,
    canWrite: canWriteSection,
  })
  const storage = useStorage(save, now)

  /** De awareness van elke sectie, voor de presence in de kopbalk en de nummering (R-72). */
  const awarenessSources = useMemo(
    () =>
      [...(connections?.values() ?? [])].flatMap((connection) =>
        connection.awareness === undefined ? [] : [connection.awareness],
      ),
    [connections],
  )
  /** Dezelfde awareness met de sectie erbij, voor de lijst in de kop: wie werkt waar (G-18, G-19). */
  const sectionPresence = useMemo(
    () =>
      projection.secties.flatMap((section) => {
        const source = connections?.get(section.id)?.awareness
        if (source === undefined) return []
        // Het label van het veld waar iemand werkt, voor "in Overzicht, bij Titel van het project".
        const fieldTitles = new Map(
          section.velden.flatMap((field) => (field.label === undefined ? [] : [[field.id, inLanguage(field.label, language)] as const])),
        )
        return [{ sectieId: section.id, titel: inLanguage(section.titel, language), source, veldTitel: (veldId: string) => fieldTitles.get(veldId) }]
      }),
    [connections, projection, language],
  )
  /**
   * Waar de lezer nu werkt, voor de anderen (G-18): `focus` in de awareness van de sectie waar de focus
   * staat, en uit in alle andere. Zo staat "X werkt hier" alleen bij de sectie waar X echt is, en niet
   * onder elke kop, want iedereen heeft alle secties open.
   *
   * En in welk veld (verzoek van de productowner van 3 november 2026): `veld` met de id van het veld met
   * de focus, in dezelfde awareness, en weg zodra de focus het veld verlaat. Het buitenste veld in de
   * sectie, zodat een cel van een herhaalgroep als de herhaalgroep telt: dat is een veld van de sectie,
   * en alleen zo'n id laat de pod door (`boundAwareness.ts`). Alleen bij een wijziging gezet, want elke
   * aanroep gaat naar iedereen op het document.
   */
  useEffect(() => {
    if (connections === undefined) return
    let current: string | undefined
    let currentField: string | undefined
    const mark = (sectionId: string | undefined, fieldId: string | undefined): void => {
      if (sectionId === current && fieldId === currentField) return
      const before = current === undefined ? undefined : connections.get(current)?.awareness
      if (sectionId === current) {
        before?.setLocalStateField?.('veld', fieldId ?? null)
        currentField = fieldId
        return
      }
      if (currentField !== undefined) before?.setLocalStateField?.('veld', null)
      before?.setLocalStateField?.('focus', false)
      current = sectionId
      currentField = fieldId
      const after = sectionId === undefined ? undefined : connections.get(sectionId)?.awareness
      after?.setLocalStateField?.('focus', true)
      if (fieldId !== undefined) after?.setLocalStateField?.('veld', fieldId)
    }
    const onFocus = (): void => {
      const active = document.activeElement
      const section = active?.closest('section.ef-sectie')
      if (section === null || section === undefined || !connections.has(section.id)) {
        mark(undefined, undefined)
        return
      }
      let field: string | undefined
      for (let at = active?.closest('[data-veld]'); at !== null && at !== undefined && section.contains(at); at = at.parentElement?.closest('[data-veld]')) {
        field = at.getAttribute('data-veld') ?? field
      }
      mark(section.id, field)
    }
    // Bij het verlaten na de wissel: dan staat `activeElement` al op het nieuwe element, of op de body.
    const onBlur = (): void => queueMicrotask(onFocus)
    document.addEventListener('focusin', onFocus)
    document.addEventListener('focusout', onBlur)
    return () => {
      document.removeEventListener('focusin', onFocus)
      document.removeEventListener('focusout', onBlur)
      mark(undefined, undefined)
    }
  }, [connections])
  /**
   * Hoe een ander in beeld heet: de naam van de gastheer als die er een geeft, anders "Deelnemer"
   * met een volgnummer over alle secties heen (`participantNames`). Nooit de persoonssleutel zelf
   * (`presentName` in `PresenceBar.tsx`).
   */
  /**
   * Wie er in de opmerkingen schrijft. Zij tellen mee in de nummering van "Deelnemer", zodat een
   * schrijver van een opmerking die nu niet aanwezig is een eigen nummer krijgt en een aanwezige
   * in de balk en bij zijn opmerking hetzelfde nummer draagt (audit van 30 oktober 2026).
   */
  const commentAuthors = useMemo(
    () => Object.values(threadsPer).flatMap((threads) => threads.flatMap((thread) => thread.berichten.map((message) => message.persoon))),
    [threadsPer],
  )
  const othersNumbered = useCallback((): ((person: string) => string) => {
    const present = awarenessSources.flatMap((source) =>
      [...source.getStates().values()].flatMap((state) => {
        const name = (state.user as { name?: unknown } | undefined)?.name
        return typeof name === 'string' ? [name] : []
      }),
    )
    return participantNames(
      [...present, ...commentAuthors].filter((person) => isPersonKey(person) && person !== identity),
      language,
    )
  }, [awarenessSources, commentAuthors, identity, language])
  const presenceName = useCallback(
    (person: string): string => {
      // Dezelfde persoon in een tweede venster of op een tweede apparaat: nooit als een ander (G-18).
      if (person === identity) return language === 'nl' ? 'U, in een ander venster' : 'You, in another window'
      const named = nameOf?.(person)
      if (named !== undefined && !isPersonKey(named)) return named
      return othersNumbered()(person)
    },
    [nameOf, identity, language, othersNumbered],
  )
  /**
   * Hoe de schrijver van een opmerking heet (R-73, R-139): zoals in de aanwezigheid, en nooit de
   * persoonssleutel. Eerder stond hier "portaal:00000000-0000-4000-8000-000000000001" terwijl de
   * balk erboven "Deelnemer 1" zei (audit van 30 oktober 2026).
   */
  const authorName = useCallback(
    (person: string): string => {
      if (person === identity) return language === 'nl' ? 'U' : 'You'
      const named = nameOf?.(person)
      if (named !== undefined && !isPersonKey(named)) return named
      return othersNumbered()(person)
    },
    [nameOf, identity, language, othersNumbered],
  )
  /**
   * Of de banner voor wie via een deling binnenkwam dicht is: per instantie en per persoon onthouden,
   * want wie hem sloot heeft hem gelezen. Zonder `localStorage` (een privévenster) blijft hij dicht
   * tot de pagina opnieuw opkomt.
   */
  const bannerKey = instantieId === undefined ? undefined : `ef-deelbanner:${instantieId}:${identity}`
  const [bannerClosed, setBannerClosed] = useState(() => {
    if (bannerKey === undefined) return false
    try {
      return window.localStorage.getItem(bannerKey) === 'dicht'
    } catch {
      return false
    }
  })
  const closeBanner = useCallback(() => {
    setBannerClosed(true)
    if (bannerKey !== undefined) {
      try {
        window.localStorage.setItem(bannerKey, 'dicht')
      } catch {
        // Geen opslag: dan alleen voor deze keer.
      }
    }
    // De knop verdwijnt met de banner; de focus naar de kop van de eerste sectie (WCAG 2.4.3).
    document.querySelector<HTMLElement>('.ef-formulier__kolom .ef-sectie__knop')?.focus()
  }, [bannerKey])
  const fillMode = FILL_MODES.has(mode)

  /** Of de kopbalk op een smal scherm zijn hulpmiddelen toont (taal, wie er meewerkt, uitloggen); zie `.ef-schilbalk__meer`. */
  const [toolsOpen, setToolsOpen] = useState(false)
  const toolsId = useId()

  /**
   * Elke schrijfactie legt de herkomst vast en meldt zich bij de opslag. Beide op één
   * plek, zodat geen enkel schrijfpad er een van kan vergeten: een waarde zonder
   * herkomst haalt het schema van een revisie niet, en een wijziging die zich niet
   * meldt wordt nooit weggeschreven.
   */
  const write = useCallback(
    (action: (origin: Origin) => void): void => {
      action({ door: identity, gewijzigdOp: now() })
      storage.reportChange()
    },
    [identity, now, storage],
  )

  /**
   * Terugkeren op de plek van de laatste invoer (R-164, M8.7).
   *
   * De sleutel is per instantie én per persoon (ontwerp 6.3), zodat een gedeelde instantie
   * niet de plek van de een aan de ander toont. Ontbreekt `instantieId`, dan is er geen
   * sleutel en doet deze hele opdracht niets (zie de docstring bij die prop).
   *
   * **`writeField` en niet `schrijf` zelf.** Alleen de bind-laag in `renderField` roept
   * `writeField` aan, met het veld-id dat daar toch al in scope is; de voorvul-effect
   * hierboven blijft de kale `schrijf` gebruiken. Zonder dat onderscheid zou een voorgevulde
   * waarde (M8.6) zich hier melden als "laatste invoer", en dan scrolt een volgende opening
   * naar een veld dat de invuller zelf nooit heeft aangeraakt.
   */
  const lastInputKey = useMemo(
    () => (instantieId === undefined ? undefined : `ef-laatste-invoer:${instantieId}:${identity}`),
    [instantieId, identity],
  )
  const writeField = useCallback(
    (fieldId: string, action: (origin: Origin) => void): void => {
      write(action)
      if (lastInputKey === undefined) return
      try {
        window.localStorage.setItem(lastInputKey, fieldId)
      } catch {
        // Geen toegang tot `localStorage` (een privévenster, geblokkeerde site data): R-164 is
        // dan niet te bieden, en dat mag het invullen zelf niet raken.
      }
    },
    [write, lastInputKey],
  )

  /**
   * Alleen bij het opnieuw openen, niet bij een sprong in een reeks.
   *
   * "Bij het opnieuw openen van een formulier" (R-164) is dit component laden, niet elke keer
   * dat `documenten` van identiteit wisselt binnen dezelfde geopende pagina (M7's reekssprong,
   * `onNavigate`). Zonder deze grendel zou een sprong naar een dossier met een eigen bewaarde plek
   * de focus wegtrekken van waar de invuller net was, midden in een sprong die R-122 juist
   * zonder invoerverlies belooft. De grendel gaat één keer om, bij de eerste `gereed`, en blijft
   * daarna dicht voor de hele levensduur van dit component.
   *
   * **Geen herkansing als de sectie er bij die eerste `gereed` nog niet stond.** Komt `kies` op
   * dat moment uit op een sectie-element dat `useActiveSection` nog niet heeft aangemeld (een
   * korte race tussen twee onafhankelijke effecten), dan no-opt `kies` stil en is de grendel al
   * dicht: er komt geen tweede poging. Bewust zo gekozen boven een hertelling: het gevolg is dat
   * de invuller bovenaan het formulier begint in plaats van de sectie te missen, en dat is geen
   * dataverlies, alleen een gemiste bonus. Een tweede kanspoging zou de eenmaligheid moeten
   * loslaten en daarmee het risico van de vorige paragraaf terug in huis halen.
   */
  const lastInputReadRef = useRef(false)
  const [beginSection, setBeginSection] = useState<string | undefined>(undefined)
  useEffect(() => {
    if (lastInputReadRef.current || ready !== true) return
    lastInputReadRef.current = true
    if (lastInputKey === undefined) return
    let fieldId: string | null = null
    try {
      fieldId = window.localStorage.getItem(lastInputKey)
    } catch {
      fieldId = null
    }
    if (fieldId === null) return
    const section = projection.secties.find((s) => s.velden.some((field) => field.id === fieldId))
    setBeginSection(section?.id)
  }, [ready, lastInputKey, projection])

  /**
   * Voorvullen via de querystring (R-37, M8.6): eenmalig per verbinding, alleen in een leeg veld.
   *
   * **Wat de `ref` onthoudt, en waarom per `documenten`-identiteit.** Per verbinding welke
   * velden al zijn geschreven en met welke waarde, zodat een latere render binnen diezelfde
   * verbinding (`gereed` gaat van `false` naar `true`, of een niet-gerelateerde her-render) een
   * al geschreven veld niet als "nog te doen" ziet en niets extra's schrijft. Deze schil wordt
   * hergebruikt over een sprong in een reeks (`documenten` krijgt dan een nieuwe `Map`,
   * `FormPage`), en de bijgehouden waarden van de vorige instantie horen dan niet meer
   * mee te tellen: de `ref` wordt daarom leeggemaakt zodra de `documenten`-identiteit verandert,
   * en `prefilledFields` hieronder leest de bijgehouden waarden ook alleen als ze bij de
   * huidige `documenten` horen. Zonder die tweede controle zou een veld in de nieuwe instantie
   * dat toevallig al precies de waarde draagt die in de vorige instantie is geschreven, ten
   * onrechte als voorgevuld getoond worden (bewezen met een eigen test).
   *
   * **De markering zelf komt niet uit de `ref` maar wordt elke render tegen `stand` afgezet.**
   * De `ref` onthoudt alleen wát er is geschreven en met welke waarde; of de markering nu
   * verschijnt volgt uit de vergelijking met de levende stand (`prefilledFields` hieronder). Wist
   * de invuller het veld weer leeg, of typt hij er iets anders overheen, dan wijkt de stand af van
   * de geschreven waarde en verdwijnt de markering vanzelf: hij mag nooit een bewering over de
   * huidige waarde blijven doen nadat die waarde niet meer klopt.
   *
   * **Wacht op `gereed`.** Vóór de eerste synchronisatie weet dit product niet of "leeg" ook
   * werkelijk leeg is, of een lokaal document dat de server nog moet aanvullen; schrijven vóór
   * die garantie zou een race tegen een echt antwoord kunnen worden (twee gelijktijdige
   * schrijfacties op dezelfde Yjs-sleutel, en de laatste wint).
   *
   * **Alleen `text`, `textarea`, `radio` en `dropdown`**, met een keuzewaarde die in de opties
   * staat (`mayPrefill` in `document/prefill.ts`). Rijke tekst, een bijlage, een reeks en een
   * herhaalgroep hebben geen vorm die in één querystring-waarde past en blijven buiten beeld.
   *
   * **Met een server schrijft de pod, niet deze schil.** Heeft de sectieverbinding een `prefill`,
   * dan gaan de waarden daarlangs naar de pod, die in zijn ene document kijkt of het veld leeg is
   * en het in dezelfde stap vult (`apps/runtime/src/prefill.ts`). Twee mensen die tegelijk dezelfde
   * link openen krijgen zo één keer de tekst, en niet twee keer: een `Y.Text` voegt twee
   * gelijktijdige invoegingen samen (R-72). Zonder server (het voorbeeld van de configurator, een
   * test) is er maar één schrijver, en dan schrijft de schil zelf. De controle hier vooraf is een
   * filter, geen gezag: de pod kijkt opnieuw.
   *
   * **Alleen een sectie met `recht: 'bewerken'`.** Een lezer met alleen-lezen op deze sectie
   * (een toetser op de invoerlaag, een nalezer op een bevroren stand) zou hier lokaal een waarde
   * zien verschijnen die de server nooit aanneemt: de samenwerklaag weigert de schrijfactie toch
   * (R-171, R-172), en dan staat er na een herlaadslag weer niets, wat een invuller met een
   * verdwenen antwoord doet vermoeden (valkuil 6 in een andere vorm). Beter deze lezer helemaal
   * niet laten schrijven dan een schijnwaarde tonen die niet beklijft.
   */
  const prefillRef = useRef<
    { readonly documenten: ReadonlyMap<string, Y.Doc>; readonly geschreven: Map<string, string> } | undefined
  >(undefined)
  useEffect(() => {
    if (prefillRef.current?.documenten !== documents) {
      prefillRef.current = { documenten: documents, geschreven: new Map() }
    }
    if (prefill === undefined || ready !== true) return
    const written = prefillRef.current.geschreven
    for (const section of projection.secties) {
      if (section.recht !== 'bewerken') continue
      const doc = documents.get(section.id)
      if (doc === undefined) continue
      const toPod = connections?.get(section.id)?.prefill
      const requested: Record<string, string> = {}
      for (const { field, value } of prefillCandidates(section, prefill)) {
        if (written.has(field.id)) continue
        if (!emptyForPrefill(doc, field)) continue
        if (toPod === undefined) write((origin) => writePrefill(doc, field, value, origin))
        else requested[field.id] = value
        written.set(field.id, value)
      }
      if (toPod !== undefined && Object.keys(requested).length > 0) toPod(requested)
    }
  }, [documents, ready, prefill, projection, write, connections])

  /**
   * De markering is precies de velden waar de geschreven waarde nog in de stand staat.
   *
   * `stand` loopt al mee met elke wijziging (`useStand`), ook de wijziging die de effect
   * hierboven zelf net maakte; hergebruik daarvan houdt de markering in de pas met de invuller
   * in plaats van met het moment van schrijven.
   */
  const prefilledFields = useMemo<ReadonlySet<string>>(() => {
    const written = prefillRef.current?.documenten === documents ? prefillRef.current.geschreven : undefined
    if (written === undefined || written.size === 0) return new Set()
    const active = new Set<string>()
    for (const [fieldId, value] of written) {
      if (singleValueOf(state[fieldId]) === value) active.add(fieldId)
    }
    return active
  }, [state, documents])

  /**
   * Wat er met een opmerking kan, per sectiedocument (R-73).
   *
   * Rechtstreeks naar het document van de sectie: een draadje is samenwerkinhoud en geen
   * veldwaarde, dus er hoort geen herkomststempel bij en geen opslagmelding. De auteur en het
   * moment vult de pod (M4.12a), dus die staan hier niet; de id's komen van de generator van het
   * model, want met de hand verzonnen id's voldoen zelden aan de eigen regels.
   */
  const commentActions = useCallback(
    (doc: Y.Doc) => ({
      nieuwDraadId: (): string => newId('draad'),
      onNieuweOpmerking: (
        thread: { id: string; veldId: string; citaat: string },
        text: string,
      ): void => {
        beginThread(
          doc,
          { id: thread.id, veldId: thread.veldId as FieldId, citaat: thread.citaat },
          { id: newId('bericht'), tekst: text },
        )
      },
    }),
    [],
  )

  // De eerdere antwoorden bij de vraag, alleen in de modus met historie (R-137, R-62), en wat de
  // bronsectie daarom niet nog eens toont; de groepen heten naar het label van hun criterium.
  const labels = useMemo(() => questionLabels(projection, language), [projection, language])
  const history = useMemo(
    () => (mode === 'met-historie' ? historyPerQuestion(projection, verwijzingen, blok) : undefined),
    [mode, projection, verwijzingen, blok],
  )
  const placed = useMemo(() => (history === undefined ? undefined : placedQuestions(history, labels)), [history, labels])

  /** De naam van een bronblok: het eigen blok bij zijn weergavenaam, een blok van buiten leesbaar (R-58). */
  const sourceNameOf = useCallback(
    (contract: string): string => {
      if (contract === blok && blockName !== undefined) return inLanguage(blockName, language)
      const foreign = sourceNames?.[contract]
      return foreign === undefined ? readableBlockName(contract) : inLanguage(foreign, language)
    },
    [blok, blockName, language, sourceNames],
  )

  /**
   * Of en hoe hier een opmerking kan (R-73, besluit 12.41): in een gewone sectie van de invoerlaag, in
   * de invoerbeurt. Wie schrijft, schrijft in het document, ook in de gedaante voor de interne kant;
   * wie leest, in een invulgedaante langs de pod. In de toetslaag is er de teruglegopmerking (R-75) en
   * geen gesprek tussen medeschrijvers; de pod neemt daar ook niets aan (`comments.ts`).
   *
   * Langs de pod alleen waar de projectie het toestaat (`magOpmerken`): wie de sectie alleen langs een
   * opening door beheer of langs zijn adviesverzoek leest, krijgt geen knop die pas na Plaatsen weigert
   * (R-184b, R-66b). De pod rekent het uit met dezelfde berekening als zijn weigering; ontbreekt het
   * veld, dan blijft de knop staan zoals voorheen.
   */
  const commentWay = useCallback(
    (section: { readonly id: string; readonly type: string; readonly recht: string; readonly magOpmerken?: boolean | undefined }): 'document' | 'pod' | undefined => {
      if (section.type !== 'gewoon' || locked || projection.actieveLaag !== 'invoer') return undefined
      if (section.recht === 'bewerken' && documents.has(section.id)) return 'document'
      return fillMode && postComment !== undefined && section.magOpmerken !== false ? 'pod' : undefined
    },
    [fillMode, locked, projection.actieveLaag, documents, postComment],
  )
  /**
   * De stand per sectie op het gekozen moment (paneel Geschiedenis, ontwerp 6.9).
   *
   * Een moment noemt alleen de secties die sinds het vorige veranderden; voor de rest geldt de laatste
   * stand ervoor, en het begin van de beurt noemt ze allemaal. Een sectie zonder stand op of voor dit
   * moment bestond toen nog niet in deze beurt: haar stand is leeg. Alle standen horen bij de
   * generatie die nu open is; de pod levert geen andere (`GET /versiemomenten`).
   */
  const statesAt = useCallback(
    (momentId: string): ReadonlyMap<string, HistoryState> => {
      const moments = [...(versionMoments ?? [])].sort((a, b) => a.moment.localeCompare(b.moment))
      const target = moments.find((entry) => entry.id === momentId)
      const out = new Map<string, HistoryState>()
      if (target === undefined) return out
      for (const sectionId of documents.keys()) {
        let bytes: Uint8Array | undefined
        for (const entry of moments) {
          if (entry.moment.localeCompare(target.moment) > 0) break
          const part = entry.secties.find((candidate) => candidate.sectieId === sectionId)
          if (part !== undefined) bytes = part.snapshot
        }
        // `stateFromBytes` gooit bij beschadigde bytes; de aanroeper valt dan terug op nu.
        out.set(sectionId, bytes === undefined ? Y.emptySnapshot : stateFromBytes(bytes))
      }
      return out
    },
    [versionMoments, documents],
  )
  const [historyBroken, setHistoryBroken] = useState(false)
  const historyStates = useMemo(() => {
    if (historyChoice === undefined || !inHistory) return undefined
    try {
      return statesAt(historyChoice.id)
    } catch {
      return undefined
    }
  }, [historyChoice, inHistory, statesAt])
  /** De documenten zoals ze op dat moment waren: voor de velden zonder rich text, en voor het verschil per vraag. */
  const historyDocuments = useMemo(() => {
    if (historyStates === undefined) return undefined
    const out = new Map<string, Y.Doc>()
    for (const [sectionId, snapshot] of historyStates) {
      const live = documents.get(sectionId)
      if (live !== undefined) out.set(sectionId, Y.createDocFromSnapshot(live, snapshot, new Y.Doc({ gc: false })))
    }
    return out
  }, [historyStates, documents])
  /**
   * De rich-text-editors tonen de stand zelf, op het levende document (`showHistory`), zodat de kleur
   * per auteur klopt en een medeschrijver die intussen typt niets kapot maakt. Eruit gaat langs de drie
   * beveiligingen van valkuil 8 (`showLive`, `leaveHistory`). Geen opruiming tussen twee standen: een
   * nieuwe stand vervangt de vorige direct, want een herbevestiging van het verlaten zou anders de
   * nieuwe weergave weer weghalen.
   */
  useEffect(() => {
    for (const [sectionId, doc] of documents) {
      const shown = historyStates?.get(sectionId)
      if (shown !== undefined && historyChoice !== undefined) showHistory(doc, historyChoice.wijze, shown)
      else if (showsHistory(doc)) showLive(doc)
    }
  }, [historyStates, historyChoice, documents])
  useEffect(
    () => () => {
      for (const doc of documents.values()) if (showsHistory(doc)) showLive(doc)
    },
    [documents],
  )
  const historyBannerRef = useRef<HTMLDivElement>(null)
  /**
   * Terug naar nu. De levende stand gaat hier al terug, in de handeling zelf en niet pas in het effect
   * na de render: een editor die bij die render opnieuw ontstaat (het veld wordt weer bewerkbaar) meldt
   * zich in zijn eigen effect aan, vóór dat van dit formulier, en zou anders de stand van toen nog
   * overnemen. Die neemt hij dan over terwijl het verlaten al loopt, en een late render daarvan laat de
   * tekst van toen staan zonder de markering waaraan de herbevestiging hem herkent.
   */
  const leaveHistoryView = useCallback(() => {
    for (const doc of documents.values()) if (showsHistory(doc)) showLive(doc)
    setHistoryChoice(undefined)
    setHistoryBroken(false)
  }, [documents])
  /**
   * Een sectie kreeg een ander document (een generatienaad, eisenboek 12.50): de standen van toen horen bij de
   * vorige generatie en passen niet op het nieuwe document. Terug naar nu, langs dezelfde weg als de knop, en
   * het oude document ook terug naar nu. Zonder dit kwam de gekozen stand terug zodra de pagina de
   * versiemomenten opnieuw ophaalde, met de gewisselde sectie leeg.
   */
  const shownDocuments = useRef(documents)
  useEffect(() => {
    const before = shownDocuments.current
    shownDocuments.current = documents
    if (before === documents) return
    const swapped = [...documents].some(([sectionId, doc]) => before.has(sectionId) && before.get(sectionId) !== doc)
    if (!swapped) return
    for (const doc of before.values()) if (showsHistory(doc)) showLive(doc)
    leaveHistoryView()
  }, [documents, leaveHistoryView])
  /** Een stand kiezen: eerst lezen, en bij een kapotte stand terug naar nu met een melding. */
  const chooseHistory = useCallback(
    (choice: HistoryChoice, narrow: boolean, closePanel: () => void) => {
      try {
        statesAt(choice.id)
      } catch {
        setHistoryChoice(undefined)
        setHistoryBroken(true)
        return
      }
      setHistoryBroken(false)
      setHistoryChoice(choice)
      if (!narrow) return
      // Op een smal scherm en een telefoon staat het paneel over het formulier: dicht, en de focus naar
      // de banner, zodat de lezer ziet wat hij ziet en hoe hij terugkomt.
      closePanel()
      requestAnimationFrame(() => historyBannerRef.current?.focus())
    },
    [statesAt],
  )
  /** De legenda bij de kleuren: iedereen die in een van de getoonde secties schreef. */
  const historyLegend = useMemo(() => {
    if (!inHistory) return []
    const people = new Set<string>()
    for (const doc of documents.values()) for (const person of authorsIn(doc)) people.add(person)
    return [...people].map((person) => ({ naam: authorName(person), kleur: colorFor(person) }))
  }, [inHistory, documents, authorName])
  /** De antwoorden van toen, over alle secties. */
  const historyThen = useMemo(
    () =>
      historyDocuments === undefined
        ? undefined
        : documentStateOf(
            projection.secties.flatMap((section) => {
              const doc = historyDocuments.get(section.id)
              return doc === undefined ? [] : section.velden.map((field) => ({ veld: field, doc }))
            }),
          ),
    [historyDocuments, projection],
  )
  /** Bij vergelijken: per vraag zonder rich text het antwoord van toen, waar het van nu verschilt. */
  const historyEarlier = useMemo(() => {
    if (historyChoice?.wijze !== 'verschil' || historyThen === undefined) return undefined
    const richtext = new Set(projection.secties.flatMap((section) => section.velden.filter((field) => field.control === 'richtext').map((field) => field.id)))
    return new Map(Object.entries(historyThen).filter(([fieldId, answer]) => !richtext.has(fieldId) && contentOf(answer) !== contentOf(state[fieldId])))
  }, [historyChoice, historyThen, projection, state])

  const historyPanel: FormPanel | undefined =
    versionMoments === undefined && ((earlierVersies?.length ?? 0) === 0 || onEarlierVersie === undefined)
      ? undefined
      : {
          id: 'geschiedenis',
          titel: language === 'nl' ? 'Geschiedenis' : 'History',
          knop: language === 'nl' ? 'Geschiedenis' : 'History',
          render: () => (
            <>
              {historyBroken ? (
                <p className="ef-geschiedenis__fout" role="alert">
                  {language === 'nl' ? 'Deze stand is niet te lezen. U ziet de stand van nu.' : 'This version cannot be read. You are seeing the current version.'}
                </p>
              ) : null}
              <HistoryOverview
                taal={language}
                momenten={versionMoments ?? []}
                gekozen={inHistory ? historyChoice : undefined}
                naamVan={authorName}
                onKies={(choice) => chooseHistory(choice, formOutOfView(), () => formPanelsRef.current?.())}
                {...(earlierVersies === undefined ? {} : { eerdereVersies: earlierVersies })}
                {...(chosenVersie === undefined ? {} : { gekozenVersie: chosenVersie })}
                versieNaam={(versie) => versieName(versie, language)}
                {...(onEarlierVersie === undefined ? {} : { onEerdereVersie: onEarlierVersie })}
                {...(onVersionMoments === undefined ? {} : { onOpen: onVersionMoments })}
              />
            </>
          ),
        }
  /** Het sluiten van het paneel, voor `chooseHistory`; via een ref, want de panelen komen hierna. */
  const formPanelsRef = useRef<(() => void) | undefined>(undefined)

  // De panelen Bronnen en Opmerkingen rechts, met hun knoppen naast de titel (ontwerp A, `formPanels.tsx`).
  const formPanels = useFormPanels({
    projectie: projection,
    documenten: documents,
    taal: language,
    zichtbaar: visibility.secties,
    dradenPer: threadsPer,
    citatieScopes: citationScopes,
    kanSchrijven: canWriteSection,
    opmerkingWeg: commentWay,
    ...(postComment === undefined ? {} : { onOpmerking: postComment }),
    ...(deleteComment === undefined ? {} : { onOpmerkingVerwijderen: deleteComment }),
    ik: identity,
    naamVan: authorName,
    ...(historyPanel === undefined ? {} : { extra: [historyPanel] }),
  })
  formPanelsRef.current = () => formPanels.panelen?.onSluit({ focusTerug: false })

  const renderField = useCallback(
    (
      field: Field,
      sectionNumber: number | undefined,
      sectionId: string,
      markerIds: readonly string[],
      inSourceGroup = false,
    ): ReactNode => {
      const doc = documents.get(sectionId)
      // Bij een stand uit de geschiedenis lezen de velden zonder rich text het document van toen; een
      // rich-text-veld toont de stand zelf op het levende document (`showHistory`).
      // Alleen bij bekijken; bij vergelijken staat het antwoord van nu in het veld en dat van toen eronder.
      const shownDoc = field.control === 'richtext' || historyChoice?.wijze !== 'stand' ? doc : (historyDocuments?.get(sectionId) ?? doc)
      const section = projection.secties.find((s) => s.id === sectionId)
      const sectionCitations = citationsPer.get(sectionId)
      // Een veld van het processysteem dat niet aanpasbaar is, is alleen-lezen (R-54, R-58); de pod zet
      // het ook terug als iemand het toch raakt (`apps/runtime/src/dossierGuard.ts`).
      const locked = 'dossierveld' in field && field.dossierveld !== undefined && !field.dossierveld.aanpasbaar
      // Een akkoord in een laag die deze lezer niet bezit, geeft de indiener (`foreignConsents`).
      const byIndiener = foreignConsents.has(field.id)
      const editable = section?.recht === 'bewerken' && doc !== undefined && !locked && !byIndiener
      /**
       * Een sectie die deze lezer in een invulgedaante alleen leest (een medeschrijver met leesrecht,
       * audit delen G-16): wat er nog openstaat is niet aan hem, dus geen meldingen en geen teller in
       * waarschuwingskleur, en een leeg veld zegt "Niet ingevuld" in plaats van een leeg grijs kader.
       * In de toets en bij nalezen blijven de meldingen staan: daar horen ze bij het oordeel.
       */
      // Niet na een verlopen sessie: dan is alles alleen-lezen om over te nemen, en blijft het formulier
      // staan zoals het was (`readOnlyAfterExpiry` in de pagina).
      const readShared = fillMode && !locked && section !== undefined && section.recht !== 'bewerken' && section.type !== 'bron'
      const own = readShared || byIndiener ? undefined : status.veld[field.id]
      const ownLimits = limits.get(field.id)
      // Een leeg veld in een leessectie leest zoals elk veld dat de lezer niet kan bewerken: één regel
      // "Niet ingevuld" onder de kop (`reading` hieronder, R-133). Tot de merge van 31 oktober 2026 had
      // de leessectie een eigen tak met "Nog niet ingevuld." in een alinea waar het label naar wees.

      return (
        <FieldShell
          // Een andere stand is een ander document: opnieuw opbouwen, zodat een control niet aan het vorige blijft hangen.
          // Ook een ander levend document: na een generatienaad haalt de pagina het opnieuw op (eisenboek 12.50), en
          // een rich-text-editor bindt zijn fragment één keer.
          key={`${documentIdentity(doc)}:${field.control === 'richtext' || historyDocuments === undefined || historyChoice?.wijze !== 'stand' ? 'nu' : `stand-${historyChoice.id}`}`}
          veld={field}
          nummer={sectionNumber}
          taal={language}
          verplicht={!readShared && !byIndiener && required.has(field.id)}
          sterretje={editable && takesInput(field.control)}
          hardeControles={own?.hardeControles ?? []}
          waarschuwingen={own?.waarschuwingen ?? []}
          labelViaHeader={labelViaHeader(field.control)}
          labelInControl={('akkoord' in field && field.akkoord === true) || field.control === 'akkoordPerPersoon'}
          // Bij een stand uit de geschiedenis is een veld leeg als het toen leeg was; bij vergelijken pas als
          // het toen én nu leeg is, want anders toont de editor wat er sindsdien bij kwam of verdween.
          empty={
            historyThen === undefined
              ? isEmpty(state[field.id])
              : historyChoice?.wijze === 'stand'
                ? isEmpty(historyThen[field.id])
                : isEmpty(state[field.id]) && isEmpty(historyThen[field.id])
          }
          // Wie een invoerveld niet kan bewerken, leest het antwoord (R-133): zonder hulptekst, en
          // een leeg antwoord als één regel "Niet ingevuld".
          // Zonder document is er niets gelezen, en dan is "Niet ingevuld" niet waar (valkuil 6).
          // Een akkoord van de indiener tekent zijn eigen leesweergave, ook als het nog niet is gegeven.
          reading={!editable && !byIndiener && field.control !== 'akkoordPerPersoon' && doc !== undefined && takesInput(field.control)}
          extraDescription={markerIds}
          anchor={anchorOf(field.id)}
          showAllNotices={showAllNotices}
          // Wie er nu in dit veld werkt, met dezelfde naam als bij de sectie (`presenceName`, R-72, R-139).
          {...(() => {
            const awareness = connections?.get(sectionId)?.awareness
            if (awareness === undefined) return {}
            return {
              presence: (id: string) => (
                <FieldPresence awareness={awareness} veldId={field.id} id={id} taal={language} nameOf={presenceName} ik={identity} />
              ),
            }
          })()}
        >
          {(link) => {
            const draw = controlFor(field.control)
            if (draw === undefined) return null
            const shared: Shared = {
              veld: field,
              taal: language,
              koppeling: link,
              bewerkbaar: editable,
              // De teller loopt mee met typen (R-12); wie niet kan schrijven typt niet, en "30 van 200
              // tekens, 170 over" bij een ingediend antwoord is ruis (audit van 30 oktober 2026).
              ...(ownLimits === undefined || !editable ? {} : { limieten: ownLimits }),
            }

            /**
             * Een verwijzing heeft geen document en dat is geen uitzondering maar de eis.
             *
             * Een bronsectie krijgt geen sectiedocument, want niemand schrijft haar (R-56), en
             * tot M6.3 viel een verwijzing daarom in de `doc === undefined`-tak en werd hij als
             * niets getekend. De uitkomst komt van de pod, die hem tegen de dossierdocumenten
             * heeft opgelost (`resolveVerwijzingen`); is er geen uitkomst meegegeven, dan is de
             * stand `onbeschikbaar` en niet leeg, want R-61 vraagt in beide gevallen de melding.
             */
            // Twee controls lezen uit het dossier: `verwijzing` één waarde en `subgrid` een
            // reeks (R-62). Beide krijgen dezelfde propvorm, want beide hebben geen document.
            if (field.control === 'verwijzing' || field.control === 'subgrid') {
              const fromSource = 'bron' in field ? field.bron : undefined
              const roles = field.control === 'subgrid' ? seriesRolesFor(fromSource) : undefined
              // In met-historie laat alleen een reeks van het eigen blok weg wat al bij de vraag staat.
              const foreignName = fromSource === undefined || fromSource === null ? undefined : sourceNames?.[fromSource.contract]
              const elsewhere = roles !== undefined && placed !== undefined && fromSource?.contract === blok ? placed : undefined
              return draw({
                ...shared,
                vorm: 'verwijzing',
                uitkomst: verwijzingen?.[field.id] ?? { stand: 'onbeschikbaar' },
                ...(fromSource === undefined || fromSource === null ? {} : { bron: fromSource.contract }),
                // De naam van het eigen blok kent de pod (R-125); een blok van buiten nog niet.
                ...(fromSource !== undefined && fromSource !== null && fromSource.contract === blok && blockName !== undefined
                  ? { bronNaam: inLanguage(blockName, language) }
                  : foreignName === undefined
                    ? {}
                    : { bronNaam: inLanguage(foreignName, language) }),
                ...(inSourceGroup ? { inBronGroep: true } : {}),
                ...(roles === undefined ? {} : { roles, vraagLabels: labels }),
                ...(elsewhere === undefined ? {} : { elders: elsewhere }),
              })
            }

            /**
             * Een criterium heeft ook geen document, en om een andere reden dan een verwijzing.
             *
             * Een verwijzing wordt door niemand geschreven (R-56); een criterium juist wel, maar
             * niet naar het sectiedocument. Zijn uitkomst staat haaks op de antwoorden (U23) en
             * gaat in één keer naar zijn eigen route, want er is precies één oordeel per criterium
             * per instantie (U21, R-192). Vandaar de zevende propvorm en niet `waarde`: een
             * tussentijdse `onWaarde` zou een half oordeel beloven dat de opslag weigert.
             *
             * Zonder `onVerdict` staat het criterium alleen-lezen: er is dan geen route om een
             * oordeel heen te sturen. Het voorbeeld van de configurator geeft er wél een mee, een
             * lokale die niets opslaat (D-22, D-24, zie `Voorbeeld.tsx`).
             */
            if (field.control === 'criterium') {
              const own = outcomes?.[field.criteriumId]
              return draw({
                ...shared,
                bewerkbaar: editable && onVerdict !== undefined,
                vorm: 'laag',
                ...(own === undefined ? {} : { uitkomst: own }),
                onVerdict: (verdict) => onVerdict?.(field.criteriumId, verdict),
              })
            }

            /**
             * Een vast component leest het hele sectiedocument, en niet zijn eigen veld-id.
             *
             * `Referentielijst` (het enige vandaag gebouwde component) heeft de rich-text-
             * fragmenten van de hele sectie nodig voor de citatie-engine (R-50): een citatie kan
             * in elk rich-text-veld van de sectie staan, niet alleen in het veld dat toevallig
             * naast het component is geplaatst.
             */
            if (field.control === 'component') {
              if (doc === undefined) return null
              return draw({
                ...shared,
                vorm: 'sectie',
                doc,
                richtextVeldIds: richtextFieldIdsPerSection.get(sectionId) ?? [],
                ...(sectionCitations === undefined ? {} : { citations: sectionCitations }),
              })
            }

            if (doc === undefined || shownDoc === undefined) return null
            return draw(
              bind({
                gedeeld: shared,
                doc: shownDoc,
                write: (action) => writeField(field.id, action),
                eisen: requirements.get(field.id),
                ...(uploadAttachment === undefined ? {} : { upload: (file: File) => uploadAttachment(field.id, file) }),
                aantallen: counts.get(field.id),
                taal: language,
                identiteit: identity,
                ...(verwijzingen === undefined ? {} : { verwijzingen }),
                rijbevindingen: rows,
                verplichteVelden: required,
                toonAlleMeldingen: showAllNotices,
                anchorFor: anchorOf,
                ...(byIndiener || field.control === 'herhaalgroep' ? { akkoordDoorIndiener: foreignConsents } : {}),
                // Eerst wegschrijven, dan vragen: de pod rekent de stand waarop het akkoord rust uit wat hij heeft.
                ...(field.control === 'akkoordPerPersoon' && onConsent !== undefined && editable
                  ? {
                      akkoord: async (handeling: 'geven' | 'intrekken') => {
                        await storage.writeNow()
                        await onConsent({ sectie: sectionId, veld: field.id, handeling })
                      },
                    }
                  : {}),
                opmerkingen:
                  field.control === 'richtext'
                    ? {
                        // Een opmerking hangt aan geselecteerde tekst (R-73, besluit 12.41): een schrijver zet
                        // haar in het document, een meelezer langs de pod. Het gesprek staat in het paneel
                        // Opmerkingen (`formPanels.tsx`), niet onder het veld.
                        ...(section !== undefined && commentWay(section) === 'document' ? commentActions(doc) : {}),
                        ...(section !== undefined && commentWay(section) === 'pod' && postComment !== undefined
                          ? {
                              onOpmerkingLangsPod: (bereik: CommentRange, text: string) =>
                                postComment({ sectie: sectionId, veldId: field.id, bereik, tekst: text }),
                            }
                          : {}),
                        ...(connections?.get(sectionId) === undefined
                          ? {}
                          : {
                              presence: {
                                provider: connections.get(sectionId)?.provider,
                                // De eigen sleutel, nooit een label: wat hier staat gaat in de awareness naar
                                // iedereen op het document, en een label kan een e-mailadres zijn (besluit 5).
                                // De anderen zetten er zelf hun label bij (`nameOf`).
                                naam: identity,
                                nameOf: presenceName,
                                // Als medewerker van NWO op een document dat de externe kant altijd
                                // leest: dezelfde regel als de pagina (R-66a, B6).
                                // Een sectie zonder laag heeft geen document; dicht is de veilige kant.
                                anoniem: isNwoEmployee(
                                  ownPresence(identity, projection.kant, (section === undefined ? null : laagOf(section.type)) ?? 'invoer'),
                                ),
                              },
                            }),
                      }
                    : undefined,
                rijCitatie:
                  field.control === 'herhaalgroep'
                    ? (rowFieldId: string, rowId: string): CitationProps => {
                        // Eén sleutel per cel, zodat het paneel onder de cel komt die erom vroeg.
                        const cell = `${rowFieldId}@${rowId}`
                        return {
                          chooseReference: sourcePicker.chooseReferenceFor(sectionId, cell),
                          newCitationId: () => newId('citatie'),
                          sourcePanel: sourcePicker.panelFor(sectionId, cell),
                          ...(sectionCitations === undefined
                            ? {}
                            : { citationLabels: sectionCitations.labels, citationEntries: sectionCitations.entries }),
                        }
                      }
                    : undefined,
                citatie:
                  field.control === 'richtext'
                    ? {
                        chooseReference: sourcePicker.chooseReferenceFor(sectionId, field.id),
                        newCitationId: () => newId('citatie'),
                        sourcePanel: sourcePicker.panelFor(sectionId, field.id),
                        ...(sectionCitations === undefined
                          ? {}
                          : { citationLabels: sectionCitations.labels, citationEntries: sectionCitations.entries }),
                      }
                    : undefined,
              }),
            )
          }}
        </FieldShell>
      )
    },
    [
      documents,
      historyDocuments,
      historyChoice,
      historyThen,
      projection,
      status,
      foreignConsents,
      onConsent,
      storage,
      language,
      required,
      limits,
      requirements,
      counts,
      richtextFieldIdsPerSection,
      citationsPer,
      writeField,
      threadsPer,
      commentActions,
      sourcePicker,
      connections,
      nameOf,
      identity,
      rows,
      showAllNotices,
      anchorOf,
      verwijzingen,
      labels,
      placed,
      blok,
      blockName,
      presenceName,
      authorName,
      fillMode,
      state,
      locked,
      commentWay,
      postComment,
    ],
  )


  /**
   * Hoeveel draadjes er per sectie nog openstaan (R-73).
   *
   * Hier en niet in de engine: `evaluate` rekent over de uitgeschreven stand en daar staan
   * draadjes niet in. Ze leven in het sectiedocument, en de browser heeft dat document al open,
   * dus dit getal is een aftakking van een abonnement dat er toch is.
   *
   * De `useCallback` is netheid en geen noodzaak: geen van de componenten eronder zit in
   * `React.memo` en `outstandingPer` staat in geen enkele dependencylijst, dus een wisselende
   * functie-identiteit kost hier niets. De lus die `useSnapshot` voorkomt zit een laag
   * lager, bij de vergelijking van de gelezen waarde, en niet bij deze prop.
   */
  const outstandingPer = useCallback(
    (sectionId: string): number => outstandingCount(threadsPer[sectionId] ?? []),
    [threadsPer],
  )

  /**
   * De secties van de eigen laag van deze lezer (R-171, R-172): voor een invuller is dat
   * toch al zijn hele projectie (hij krijgt nooit een toetssectie, R-109/D-55), voor een
   * toetser precies de toetssecties en niet de invoerlaagsecties die hij alleen-lezen
   * meekrijgt.
   *
   * **Waarom dit hier moet, en niet in `evaluate()`.** `status.veld`/`status.sectie` blijven
   * over de volle `projectie.secties` gerekend: een harde controle op een alleen-lezen
   * invoerlaagveld hoort desondanks gewoon onder dat veld te blijven staan (die informatie
   * gaat nergens anders heen). Wat hier wordt gefilterd is alleen "wat moet DEZE lezer nog
   * zelf afronden om te kunnen indienen" (c1, c2, c4, R-157) — en dat is nooit een veld dat
   * hij niet mag bewerken. Zonder dit filter zag een toetser bij "Toetsen door toetser" de
   * nog onvervulde verplichte velden van de invuller meetellen, ook al zijn die bij een
   * echte indiening al lang voldaan (R-23, R-31) en kan de toetser ze sowieso niet bewerken.
   */
  const ownLaagSectionIds = useMemo(() => {
    const laag = ownLaag(projection)
    return new Set(
      projection.secties
        // Wie niet indient (een medeschrijver, R-69) heeft alleen af te ronden wat hij kan schrijven: een
        // leessectie die nog leeg is, is niet aan hem (audit delen G-16). Voor wie wel indient verandert
        // er niets: zijn poort telt zoals de pod (R-31).
        .filter((section) => laagOf(section.type) === laag && (projection.magIndienen || section.recht === 'bewerken'))
        .map((section) => section.id),
    )
  }, [projection])

  /**
   * Hoeveel verplichte velden er per sectie zijn ingevuld, en van hoeveel, voor de neutrale teller in
   * de inhoudsopgave en de zin in de actiebalk (ontwerp van het invulscherm, 29 oktober 2026).
   *
   * Uit de engine: verplicht komt uit `requiredFields` (R-26), ingevuld uit dezelfde regels (`requiredChecks`), zichtbaar uit
   * de evaluatie (R-34: een verborgen veld telt niet mee). Alleen voor een sectie van de eigen laag
   * die deze lezer mag bewerken; een alleen-lezen sectie heeft niets in te vullen. Of de sectie klaar
   * is zegt deze teller niet: dat doet het vinkje, en dat hangt aan de harde controles.
   */
  const progressPer = useCallback(
    (sectionId: string): SectionProgress | undefined => {
      if (!ownLaagSectionIds.has(sectionId)) return undefined
      const section = projection.secties.find((candidate) => candidate.id === sectionId)
      if (section === undefined || section.recht !== 'bewerken') return undefined
      const ids = section.velden
        .filter((field) => required.has(field.id) && !foreignConsents.has(field.id) && fieldVisible(visibility, field.id))
        .map((field) => field.id)
      // Ingevuld als de regels die op leeg falen voldoen, en niet als het antwoord niet leeg is: een
      // akkoord op nee is niet leeg maar ook niet gegeven (audit van 30 oktober 2026).
      const filled = (fieldId: string): boolean =>
        (requiredRules.get(fieldId) ?? []).every((rule) => fieldCheckPasses(rule, state[fieldId]))
      return { ingevuld: ids.filter(filled).length, totaal: ids.length }
    },
    [ownLaagSectionIds, projection, required, requiredRules, visibility, state, foreignConsents],
  )

  /**
   * Of deze lezer verplichte velden heeft om in te vullen, voor de uitlegzin bovenaan (besluit van 1
   * november 2026, R-09). Dezelfde voorwaarde als het sterretje bij een veld (bewerkbaar, vraagt
   * invoer, verplicht), met de rijvelden van een herhaalgroep erbij: staat er ergens een sterretje,
   * dan staat de uitleg erboven, en anders niet.
   */
  const requiredNote = useMemo(
    () =>
      !locked &&
      projection.secties.some(
        (section) =>
          section.recht === 'bewerken' &&
          ownLaagSectionIds.has(section.id) &&
          section.velden.some(
            (field) =>
              // Een vastgezet veld uit het processysteem bewerkt niemand, dus daar staat geen sterretje.
              (required.has(field.id) && !foreignConsents.has(field.id) && takesInput(field.control) && !('dossierveld' in field && field.dossierveld !== undefined && !field.dossierveld.aanpasbaar)) ||
              (field.control === 'herhaalgroep' &&
                field.rijVelden.some((rowField) => required.has(rowField.id) && !foreignConsents.has(rowField.id) && takesInput(rowField.control))),
          ),
      ),
    [projection, ownLaagSectionIds, required, locked, foreignConsents],
  )

  /**
   * Hetzelfde getal over het hele formulier, maar dan beperkt tot de eigen laag (R-157,
   * R-171, R-172): de voet naast de indienknop en de indienknop zelf horen hetzelfde te
   * tellen als de inhoudsopgave hierboven, anders spreken twee plekken op hetzelfde scherm
   * elkaar tegen.
   */
  const outstandingHardChecksOwnLaag = useMemo(
    () =>
      [...ownLaagSectionIds].reduce(
        (sum, sectionId) => sum + (status.sectie[sectionId]?.openstaandeHardeControles ?? 0),
        0,
      ),
    [status, ownLaagSectionIds],
  )

  /**
   * De criteria van de eigen laag die nog geen menselijk oordeel hebben (besluit van 9 oktober
   * 2026, R-147, R-152, R-180, R-187).
   *
   * Met `beletselVoorOverdracht` uit de engine, dezelfde regel waarmee de pod weigert: elke
   * overdracht vraagt een oordeel op elk criterium van de laag die sluit, ook indienen. Welke
   * gebeurtenis hier staat maakt voor die eerste controle niet uit, want alle zes vragen hetzelfde
   * over dezelfde criteria; het is de handeling die onderaan deze gedaante staat. Een uitkomst met
   * alleen een machine-deel telt niet (R-180). Een aanvraagformulier heeft in de invoerlaag geen
   * criteria, dus daar is deze lijst altijd leeg.
   */
  const withoutVerdict = useMemo((): readonly string[] => {
    const criteria = criterionIdsIn(projection.secties, ownLaag(projection))
    const event = mode === 'toetsen' ? 'goedgekeurd' : 'ingediend'
    const blocker = blockerForHandover(event, criteria, Object.values(outcomes ?? {}), {
      teruggaven: [],
      stand: {},
    })
    return blocker?.code === 'oordeel-ontbreekt' ? blocker.criteria : []
  }, [projection, outcomes, mode])

  /** Per criterium zonder oordeel: de sectie, het veld en de naam, in de volgorde van de definitie. */
  const criteriaWithoutVerdict = useMemo(() => {
    const missing = new Set(withoutVerdict)
    return projection.secties.flatMap((section) =>
      ownLaagSectionIds.has(section.id)
        ? section.velden.flatMap((field) =>
            field.control === 'criterium' && missing.has(field.criteriumId)
              ? [{ sectieId: section.id, veldId: field.id, naam: field.label === undefined ? field.criteriumId : inLanguage(field.label, language) }]
              : [],
          )
        : [],
    )
  }, [projection, ownLaagSectionIds, withoutVerdict, language])

  /**
   * Voor de toetser: hoeveel criteria van zijn laag een oordeel hebben, en per getoetste sectie de
   * stand van de criteria die erover gaan (ontwerp van het toetsscherm, R-98, R-109). Een oordeel
   * telt als er een menselijk oordeel is; alleen een machine-deel niet (R-180).
   */
  const reviewCriteria = useMemo(() => {
    if (mode !== 'toetsen') return undefined
    const own = ownLaag(projection)
    const verdictOf = (criteriumId: string) => outcomes?.[criteriumId]?.oordeel
    const ids = criterionIdsIn(projection.secties, own)
    const perSection = new Map<string, CriteriaState>()
    for (const section of projection.secties) {
      if (section.type === 'toets') continue
      const reviews = projection.secties.filter((candidate) => candidate.type === 'toets' && candidate.hoortBij === section.id)
      const criteria = reviews.flatMap((review) => review.velden.flatMap((field) => (field.control === 'criterium' ? [field.criteriumId] : [])))
      perSection.set(section.id, stateOf(criteria.map(verdictOf)))
    }
    for (const section of projection.secties) {
      if (section.type !== 'toets' || section.hoortBij !== undefined) continue
      perSection.set(section.id, stateOf(section.velden.flatMap((field) => (field.control === 'criterium' ? [verdictOf(field.criteriumId)] : []))))
    }
    return {
      progress: { beoordeeld: ids.filter((id) => verdictOf(id) !== undefined).length, totaal: ids.length },
      per: (sectionId: string) => perSection.get(sectionId),
    }
  }, [mode, projection, outcomes])

  /** Wanneer de invoerlaag is ingediend: de laatste revisie die indienen was (R-173, R-174). */
  const submittedOn = useMemo(
    () =>
      mode === 'toetsen' || mode === 'nalezen'
        ? [...(earlierVersies ?? [])].reverse().find((versie) => versie.gebeurtenis === 'ingediend' || versie.gebeurtenis === 'opnieuw-ingediend')?.moment
        : undefined,
    [mode, earlierVersies],
  )

  /**
   * De regels van het overzicht van wat er nog openstaat (R-24, R-140).
   *
   * Uit dezelfde `status` als de tellingen hierboven, over dezelfde secties van de eigen laag. Het
   * aantal regels is sinds 2 november 2026 het enige getal over wat er openstaat (`openPerSection`
   * hieronder). Per sectie eerst de
   * bevindingen op sectieniveau (die horen bij geen veld; de link gaat naar de sectie), daarna per
   * veld in de volgorde van de definitie. Een regel op een rijveld wijst naar de eerste rij waarin
   * hij faalt en noemt ze allemaal; welke rijen dat zijn komt uit `rijen` van dezelfde evaluatie.
   */
  const outstandingRules = useMemo((): readonly OutstandingEntry[] => {
    const out: OutstandingEntry[] = []
    for (const section of projection.secties) {
      if (!ownLaagSectionIds.has(section.id)) continue
      const sectionStatus = status.sectie[section.id]
      if (sectionStatus === undefined) continue
      for (const finding of sectionStatus.hardeControles) {
        out.push({
          sleutel: finding.regelId,
          sectieId: section.id,
          anker: section.id,
          naam: inLanguage(section.titel, language),
          melding: finding.melding,
        })
      }
      for (const field of section.velden) {
        // Het akkoord van de indiener staat voor een ander niet open (`foreignConsents`).
        if (foreignConsents.has(field.id)) continue
        const fieldName = field.label === undefined ? field.id : inLanguage(field.label, language)
        for (const finding of status.veld[field.id]?.hardeControles ?? []) {
          out.push({
            // Met het veld erbij: de controle op de tekenset (`rgl-tekens`) kan op meer velden tegelijk staan.
            sleutel: `${finding.regelId}:${field.id}`,
            sectieId: section.id,
            anker: anchorOf(field.id),
            naam: fieldName,
            melding: finding.melding,
          })
        }
        if (field.control !== 'herhaalgroep') continue
        const order = (rowsOf(state[field.id]) ?? []).map((row) => row.rijId)
        for (const rowField of field.rijVelden) {
          if (foreignConsents.has(rowField.id)) continue
          const perRow: Readonly<Record<string, RowFindings>> = rows[rowField.id] ?? {}
          for (const finding of status.veld[rowField.id]?.hardeControles ?? []) {
            const failing = order
              .map((rowId, place) => ({ rijId: rowId, nummer: place + 1 }))
              .filter(({ rijId: rowId }) =>
                (perRow[rowId]?.hardeControles ?? []).some((own) => own.regelId === finding.regelId),
              )
            const first = failing[0]
            out.push({
              sleutel: `${finding.regelId}:${rowField.id}`,
              sectieId: section.id,
              anker: first === undefined ? anchorOf(field.id) : anchorOf(rowField.id, first.rijId),
              naam: `${fieldName}, ${rowLabel(failing.map(({ nummer: sectionNumber }) => sectionNumber), language)}, ${inLanguage(rowField.label, language)}`,
              melding: finding.melding,
            })
          }
        }
      }
    }
    // De criteria zonder oordeel, bij hun sectie; de link gaat naar het criterium zelf.
    for (const { sectieId: sectionId, veldId: fieldId, naam: name } of criteriaWithoutVerdict) {
      const place = out.findLastIndex((rule) => rule.sectieId === sectionId)
      const rule: OutstandingEntry = {
        sleutel: `oordeel-${fieldId}`,
        sectieId: sectionId,
        anker: anchorOf(fieldId),
        naam: name,
        melding: NO_VERDICT,
      }
      if (place === -1) out.push(rule)
      else out.splice(place + 1, 0, rule)
    }
    return out
  }, [projection, ownLaagSectionIds, status, rows, state, language, anchorOf, criteriaWithoutVerdict, foreignConsents])

  /**
   * Eén telling voor elk getal over wat er openstaat (besluit van 2 november 2026, R-24): de kop van het
   * overzicht, de voortgang in de kop en in de compacte kop, de zin in de voet ("Elders staan nog …") en
   * het getal per sectie in de inhoudsopgave tellen allemaal de regels van het overzicht hierboven.
   *
   * Tot deze dag kwamen ze uit twee berekeningen. De voet telde de tellers per sectie van de engine
   * (`openstaandeHardeControles`) plus de criteria zonder oordeel; het overzicht telde de regels die
   * het zelf opbouwt uit de bevindingen per veld. Die twee vallen alleen samen zolang elke bevinding
   * die de engine bij een sectie optelt ook als bevinding aan een veld of de sectie zelf hangt die de
   * lijst langsloopt. Met het schema van vandaag (een regel wijst alleen naar een veld van de eigen
   * sectie) is dat zo, maar twee berekeningen voor één getal konden uiteenlopen, en in een pod liep
   * het uiteen (20 en 21). Nu is er één bron, en kan een ander getal op hetzelfde scherm niet meer.
   */
  const openPerSection = useMemo(() => {
    const per = new Map<string, number>()
    for (const rule of outstandingRules) per.set(rule.sectieId, (per.get(rule.sectieId) ?? 0) + 1)
    return per
  }, [outstandingRules])

  /** Wat deze lezer nog moet afronden voordat hij zijn beurt kan sluiten: het aantal regels van het overzicht. */
  const outstandingConditions = outstandingRules.length

  /**
   * Wat de inhoudsopgave per sectie als open telt, of niets als er in die sectie niets te doen is
   * (audit van 30 oktober 2026, R-24).
   *
   * Open is wat de beurt tegenhoudt: de regels van het overzicht in die sectie, dus de harde controles
   * en de criteria zonder oordeel. Niets te doen is een sectie van een andere laag, een sectie die deze
   * lezer niet mag bewerken, of een sectie zonder verplicht veld en zonder criterium: die kreeg het
   * vinkje omdat er niets openstond, en een leeg formulier zei zo "3 van 12 secties compleet".
   */
  const tableOfContentsOpenPer = useCallback(
    (sectionId: string): number | undefined => {
      if (!ownLaagSectionIds.has(sectionId)) return undefined
      const section = projection.secties.find((candidate) => candidate.id === sectionId)
      // Wie een sectie niet kan bewerken, heeft er niets te doen.
      if (section === undefined || section.recht !== 'bewerken') return undefined
      const open = openPerSection.get(sectionId) ?? 0
      if (open > 0) return open
      const work = (progressPer(sectionId)?.totaal ?? 0) > 0 || section.velden.some((field) => field.control === 'criterium')
      return work ? 0 : undefined
    },
    [ownLaagSectionIds, projection, openPerSection, progressPer],
  )

  /**
   * Wat elke toetsoverdracht nu tegenhoudt (7.8 voorstel 1, R-181, R-182, R-75, R-132).
   *
   * Met `beletselVoorOverdracht` uit de engine, de functie waarmee de pod weigert, over de
   * criteria van de eigen laag van deze lezer, de uitkomsten die de pod meegaf en de stand van de
   * documenten in deze browser. Geen tweede regel: wat de pod vraagt, vraagt deze knop ook, en de
   * pod rekent het na.
   *
   * **De stand loopt mee met elke aanslag** (`useStand`), dus wie de opmerking voor de aanvrager
   * typt ziet Terugleggen meteen aangaan. Dat de pod dezelfde tekst ziet als hij de knop indrukt,
   * regelt de pagina: die schrijft eerst weg en draagt dan over (`closeBeurt` in
   * `Formulierpagina.tsx`).
   */
  const reviewBlockers = useMemo(() => {
    const criteria = criterionIdsIn(projection.secties, ownLaag(projection))
    const list = Object.values(outcomes ?? {})
    const comments = { teruggaven: handbacksIn(projection.secties), stand: state }
    return Object.fromEntries(
      REVIEW_HANDOVERS.map((event) => [
        event,
        blockerForHandover(event, criteria, list, comments),
      ]),
    ) as Record<ReviewHandover, HandoverBlocker | undefined>
  }, [projection, outcomes, state])

  /** De titel van een sectie in de taal van de lezer, voor de zin bij een knop die uit staat. */
  const sectionName = useCallback(
    (sectionId: string): string => {
      const section = projection.secties.find((candidate) => candidate.id === sectionId)
      return section === undefined ? sectionId : inLanguage(section.titel, language)
    },
    [projection.secties, language],
  )

  /**
   * Hetzelfde getal over het hele formulier, voor de voet naast de indienknop.
   *
   * Over `threadsPer` en niet over de projectie: wat hier wordt geteld is wat deze lezer in zijn
   * eigen documenten heeft, en dat is per constructie zijn projectie (R-179). Een lezer met
   * minder rechten heeft minder documenten en ziet dus een lager getal, en dat is juist.
   */
  const outstandingComments = useMemo(
    () => Object.values(threadsPer).reduce((sum, threads) => sum + outstandingCount(threads), 0),
    [threadsPer],
  )

  /**
   * Een leessectie in een invulgedaante: "Alleen lezen" bij de kop (G-16, WCAG 1.3.1). Een bronsectie
   * zegt het al met haar bronregel.
   */
  const readOnlyMarker = useCallback(
    (sectionId: string): { readonly opmerkingen: boolean } | undefined => {
      const section = projection.secties.find((candidate) => candidate.id === sectionId)
      if (locked || section === undefined || section.recht === 'bewerken' || section.type === 'bron') return undefined
      // Een opmerking begint bij geselecteerde tekst, dus alleen met een rich-text-veld in de sectie.
      return { opmerkingen: commentWay(section) !== undefined && section.velden.some((field) => field.control === 'richtext') }
    },
    [projection, commentWay, locked],
  )

  const pageTitle = [
    inLanguage(projection.titel, language),
    inLanguage(blockName ?? { nl: 'Formulier', en: 'Form' }, language),
    'NWO',
  ].join(' · ')
  const titleWithError = request > 0 && outstandingConditions > 0 ? `${language === 'nl' ? 'Fout' : 'Error'}: ${pageTitle}` : pageTitle
  useEffect(() => {
    onDocumentTitle?.(titleWithError)
  }, [onDocumentTitle, titleWithError])

  // Als sleutel en niet als verzameling: een nieuwe Set bij elke evaluatie is geen verandering.
  const hiddenKey = projection.secties
    .filter((section) => !visibility.secties.has(section.id))
    .map((section) => section.id)
    .join(' ')
  useEffect(() => {
    onHiddenSections?.(hiddenKey === '' ? [] : hiddenKey.split(' '))
  }, [onHiddenSections, hiddenKey])

  /**
   * De eerdere waarde alleen bij een veld dat sindsdien is veranderd (besluit van 30 oktober 2026,
   * R-96). "Eerder ingediend: …" stond boven élk veld, ook waar niets was veranderd, en bij een
   * lang formulier verdubbelde dat de tekst. Gelijk is gelijk in inhoud: wie het schreef en wanneer
   * telt niet.
   */
  const changedEarlierValues = useMemo(
    () =>
      earlierValues === undefined
        ? undefined
        : new Map([...earlierValues].filter(([fieldId, frozen]) => contentOf(frozen) !== contentOf(state[fieldId]))),
    [earlierValues, state],
  )

  /** De naam van de call voor de contextregel, uit de verwijzing als de definitie hem draagt. */
  const callTitle = useMemo(() => callTitleOf(projection, verwijzingen), [projection, verwijzingen])

  /**
   * De neutrale voortgang in de kop (besluit van 2 november 2026, R-24): hoeveel secties ingevuld en
   * hoeveel punten er openstaan, uit dezelfde telling als de inhoudsopgave en het overzicht. In de
   * toetsgedaante de criteria, zoals de inhoudsopgave daar. Bij nalezen niets: er is niets meer te doen.
   */
  const headerProgress = useMemo((): HeaderProgress | undefined => {
    if (mode === 'nalezen') return undefined
    const sections = projection.secties.filter((section) => visibility.secties.has(section.id))
    const part =
      reviewCriteria !== undefined
        ? reviewCriteria.progress.totaal === 0
          ? undefined
          : { klaar: reviewCriteria.progress.beoordeeld, totaal: reviewCriteria.progress.totaal }
        : completeSections(sections, tableOfContentsOpenPer)
    if (part === undefined && outstandingConditions === 0) return undefined
    const open = outstandingConditions
    // "In uw secties" voor een medeschrijver (R-69); de toetser sluit zijn eigen beurt en telt zijn hele laag.
    const ownOnly = !projection.magIndienen && reviewCriteria === undefined
    const first =
      part === undefined
        ? undefined
        : reviewCriteria !== undefined
          ? language === 'nl'
            ? `${String(part.klaar)} van ${String(part.totaal)} criteria beoordeeld`
            : `${String(part.klaar)} of ${String(part.totaal)} criteria assessed`
          : inLanguage(PROGRESS(part.klaar, part.totaal), language)
    const second =
      language === 'nl'
        ? open === 0
          ? 'niets meer open'
          : `${open === 1 ? '1 punt' : `${String(open)} punten`} open${ownOnly ? ' in uw secties' : ''}`
        : open === 0
          ? 'nothing open'
          : `${open === 1 ? '1 item' : `${String(open)} items`} open${ownOnly ? ' in your sections' : ''}`
    return {
      tekst: first === undefined ? second : `${first} · ${second}`,
      ...(part === undefined ? {} : { deel: part }),
      fout: request > 0 && open > 0,
      ...(open === 0
        ? {}
        : {
            toon: { label: (overviewOpen ? HIDE_OUTSTANDING : SHOW_OUTSTANDING)[language], onToon: showOutstanding, open: overviewOpen, controls: overviewId },
          }),
    }
  }, [mode, projection, visibility, reviewCriteria, tableOfContentsOpenPer, outstandingConditions, language, request, showOutstanding, overviewOpen, overviewId])

  const contextHeader = (
    <ContextHeader
      taal={language}
      titel={projection.titel}
      {...(dossierId === undefined ? {} : { dossierId })}
      {...(uitvraagRef === undefined ? {} : { uitvraagRef })}
      {...(blockName === undefined ? {} : { bloknaam: blockName })}
      {...(deadline === undefined ? {} : { deadline })}
      {...(series === undefined ? {} : { series })}
      {...(onNavigate === undefined ? {} : { onNavigate })}
      {...(seriesBusy === undefined ? {} : { seriesBusy })}
      {...(mode === 'toetsen' ? { toets: submittedOn === undefined ? {} : { ingediendOp: submittedOn } } : {})}
      {...(projection.namens === null || projection.namens === undefined ? {} : { namens: true })}
      {...(mode === 'nalezen' && submittedNotice
        ? {
            ingediend: {
              ...(submittedOn === undefined ? {} : { op: submittedOn }),
              ...(projection.getoetst === undefined ? {} : { getoetst: projection.getoetst }),
            },
          }
        : {})}
      {...(callTitle === undefined ? {} : { call: callTitle })}
      nu={new Date(now())}
      verplicht={requiredNote}
      gereedschap={
        // Eén werkbalk voor wat over het hele formulier gaat (besluit van 2 november 2026): de panelen
        // en, als de pagina hem geeft, Delen. Op elke breedte hier, ook op een telefoon, en niet meer
        // achter de knop Meer: daar staan alleen nog taal, wie er meewerkt en uitloggen.
        formPanels.panelen === undefined && shareButton === undefined ? undefined : (
          <div className="ef-kopwerkbalk">
            {formPanels.kopKnoppen}
            {shareButton === undefined ? null : <div className="ef-kopwerkbalk__delen">{shareButton}</div>}
          </div>
        )
      }
      {...(headerProgress === undefined ? {} : { voortgang: headerProgress })}
    />
  )


  /**
   * De actiebalk onderaan (f1, R-157, ontwerp van het invulscherm): plakt onderaan het scherm
   * zolang er formulier onder staat, met een neutrale zin over de sectie waar de invuller is, de
   * knop naar de volgende sectie en de handeling van deze gedaante.
   *
   * **"Controleren en indienen" staat aan, ook als er nog iets openstaat.** Een knop die uit staat
   * zegt niet waarom, en de invuller zoekt dan zelf. Deze knop doet bij openstaande voorwaarden
   * wat "Toon wat er nog openstaat" deed: alle meldingen aan, het overzicht in beeld en de focus
   * erop (R-24, R-140); er gaat dan niets naar de pod. Pas zonder openstaande voorwaarde dient hij
   * in. R-31 en R-81 blijven zo staan: indienen kan niet zolang een harde controle faalt, en de pod
   * rekent het na.
   */
  const footer = ({ active: activeSection, next, goTo }: SectionNavigation): ReactNode => {
    const progress = activeSection === undefined ? undefined : progressPer(activeSection)
    const empty = progress === undefined ? 0 : progress.totaal - progress.ingevuld
    // Uit dezelfde telling als het overzicht en de kop (`openPerSection`), zodat "Elders staan nog …"
    // optelt tot het getal erboven.
    const openInSection = activeSection === undefined ? 0 : (openPerSection.get(activeSection) ?? 0)
    /**
     * Eén zin en geen getal in de invulgedaanten (besluit van 29 oktober 2026). Ernaast stonden
     * "In deze sectie zijn nog 4 verplichte velden leeg" én "20 openstaande voorwaarden": twee
     * keer hetzelfde in andere woorden. R-24 vraagt dat de invuller ziet wélke voorwaarden nog
     * openstaan, en dat doen de melding bij het veld (R-25), de teller per sectie in de
     * inhoudsopgave en het overzicht achter "Controleren en indienen"; een getal in de voet vraagt
     * het niet, en R-81 ook niet. De zin zegt wel of er buiten deze sectie nog iets openstaat, zodat
     * een complete sectie niet doet alsof het formulier af is. In de toetsgedaante blijft de voet
     * zoals hij was.
     */
    const filling = mode !== 'toetsen' && mode !== 'nalezen'
    if (mode === 'toetsen') return reviewFooter()
    return (
      <div className="ef-schilvoet" data-gedaante={mode}>
        <div className="ef-schilvoet__info">
          {!filling ? null : (
            <p className="ef-schilvoet__sectie">
              {sectionSentence(progress === undefined ? undefined : empty, openInSection, outstandingConditions - openInSection, language, projection.magIndienen)}
            </p>
          )}
          {/*
            De vaste helptekst (f1, R-157) in de toetsgedaante en bij nalezen: autosave is bij deze
            CRDT-opslag het dragende mechanisme (ontwerp 6.3). In de invulgedaanten zegt de kopbalk
            al dat alles is opgeslagen en zegt de zin hierboven wanneer indienen kan.
          */}
          {filling ? null : <p className="ef-schilvoet__helptekst">{helpText(mode, language, previewLabel !== undefined)}</p>}
          {mode === 'nalezen' || !projection.magIndienen || (criteriaWithoutVerdict.length === 0 && !locked) ? null : (
            /*
             * Waarom indienen nog niet kan, in één zin en via `aria-describedby` aan de knop
             * gekoppeld (R-140), net als bij de toetsknoppen (besluit van 9 oktober 2026).
             */
            <p id={submitReasonId} className="ef-schilvoet__reden">
              {locked
                ? lockReason
                : sentenceWithoutVerdict(criteriaWithoutVerdict.map(({ naam: name }) => name), language)}
            </p>
          )}
          {/* Het opslagmoment staat in de kopbalk ("Opgeslagen om 14:32") en niet nog eens hier
              (audit R-43). */}
          {outstandingComments === 0 ? null : (
            <p className="ef-schilvoet__regel">
              {/*
               * Naast de knop en niet erin, en het blokkeert niets (R-23, R-31, R-81). Een
               * openstaand draadje is een gesprek en geen fout; het hoort wel op te vallen vóór
               * het sluiten (ontwerp 4.6, M4.19).
               */}
              <span className="ef-schilvoet__opmerkingen">
                {language === 'nl'
                  ? outstandingComments === 1
                    ? 'Nog 1 open opmerking'
                    : `Nog ${String(outstandingComments)} open opmerkingen`
                  : outstandingComments === 1
                    ? '1 open comment left'
                    : `${String(outstandingComments)} open comments left`}
              </span>
            </p>
          )}
        </div>
        <div className="ef-schilvoet__acties">
          {next === undefined ? null : (
            <button type="button" className="ef-actieknop ef-schilvoet__volgende" onClick={() => goTo(next)}>
              {language === 'nl' ? 'Volgende sectie' : 'Next section'}
            </button>
          )}
          {mode === 'nalezen' ? null : !projection.magIndienen ? (
            /*
             * Wie alleen via een deling binnenkwam, dient niet in (R-69): dat doet wie het
             * processysteem als invuller toekende. Eerder stond hier toch "Controleren en indienen",
             * en daarna "Indienen kan zodra deze voorwaarden voldoen" (audit delen van 30 oktober
             * 2026). De pod weigert het ook (`geen-eigenaar-van-de-indiening`).
             */
            <>
              <p className="ef-schilvoet__geenindienen">
                {language === 'nl' ? 'Indienen doet de aanvrager.' : 'The applicant submits.'}
              </p>
              {sharedEntry === undefined || ownLaagSectionIds.size === 0 ? null : (
                // Wat in zijn eigen secties nog ontbreekt, zonder iets naar de pod (mockup 07, R-24).
                <button
                  type="button"
                  className="ef-actieknop ef-schilvoet__controleer"
                  data-soort="secundair"
                  onClick={(event) => {
                    openOverview(event)
                    setRequest((previous) => previous + 1)
                  }}
                >
                  {language === 'nl' ? 'Mijn secties controleren' : 'Check my sections'}
                </button>
              )}
            </>
          ) : (
            <BusyButton
              className="ef-indienen"
              disabled={locked}
              busyText={language === 'nl' ? 'Bezig met indienen…' : 'Submitting…'}
              {...(criteriaWithoutVerdict.length === 0 && !locked ? {} : { 'aria-describedby': submitReasonId })}
              onAction={() => {
                // Met openstaande voorwaarden toont hij het overzicht en gaat er niets naar de pod;
                // dan wordt de knop ook niet bezig.
                if (outstandingConditions !== 0) {
                  openOverview()
                  setRequest((previous) => previous + 1)
                  return undefined
                }
                return (async () => {
                  await storage.writeNow()
                  await onSubmit?.(state)
                })()
              }}
            >
              {/* De spatie buiten het verborgen deel: de naamberekening knipt de tekst per element bij. */}
              {language === 'nl' ? 'Controleren' : 'Check'}{' '}
              <span className="ef-indienen__rest">{language === 'nl' ? 'en indienen' : 'and submit'}</span>
            </BusyButton>
          )}
        </div>
      </div>
    )
  }

  /**
   * De voet van de toetser (ontwerp van het toetsscherm, 29 oktober 2026): één zin links en de
   * drie uitkomsten van R-181 rechts.
   *
   * Eerder stonden er vier regels (de voortgang, een vaste helptekst, het aantal openstaande
   * voorwaarden en boven de knoppen nog een reden) en vijf knoppen. Nu is het één zin: hoeveel
   * criteria een oordeel hebben en, zolang een knop uit staat, waarom; de knoppen die uit staan
   * wijzen met `aria-describedby` naar die zin (R-140). Dat het werk automatisch wordt opgeslagen
   * zegt de kopbalk al. "Volgende sectie" staat hier niet: de toetser loopt langs de criteria in
   * de inhoudsopgave. De weg naar wat nog openstaat blijft wel, als knop in dezelfde regel
   * (R-24, R-140): de toetsknoppen sluiten de beurt en tonen niets.
   */
  const reviewFooter = (): ReactNode => {
    const why = locked ? lockReason : reviewReason(reviewBlockers, outstandingHardChecksOwnLaag !== 0, language, sectionName)
    const progress = reviewCriteria?.progress
    const parts = [
      progress === undefined
        ? undefined
        : language === 'nl'
          ? `${String(progress.beoordeeld)} van ${String(progress.totaal)} criteria beoordeeld.`
          : `${String(progress.beoordeeld)} of ${String(progress.totaal)} criteria assessed.`,
      why,
      outstandingComments === 0
        ? undefined
        : language === 'nl'
          ? outstandingComments === 1
            ? 'Nog 1 open opmerking.'
            : `Nog ${String(outstandingComments)} open opmerkingen.`
          : outstandingComments === 1
            ? '1 open comment left.'
            : `${String(outstandingComments)} open comments left.`,
    ].filter((part): part is string => part !== undefined)
    return (
      <div className="ef-schilvoet" data-gedaante={mode}>
        <div className="ef-schilvoet__info">
          <p className="ef-schilvoet__sectie">
            <span id={reviewSentenceId}>{parts.join(' ')}</span>
            {outstandingConditions === 0 ? null : (
              <>
                {' '}
                {/*
                  Dezelfde knop als in de voortgang in de kop: hetzelfde label en hetzelfde overzicht, neutraal
                  (besluit van 2 november 2026). Tot die dag opende deze hem als foutsamenvatting en die in
                  de kop neutraal, onder twee namen. De foutvorm volgt nu voor beide alleen uit `request`.
                */}
                <button type="button" className="ef-schilvoet__toon" aria-expanded={overviewOpen} aria-controls={overviewId} onClick={showOutstanding}>
                  {(overviewOpen ? HIDE_OUTSTANDING : SHOW_OUTSTANDING)[language]}
                </button>
              </>
            )}
          </p>
        </div>
        <div className="ef-schilvoet__acties">
          <ReviewButtons
            taal={language}
            blockers={reviewBlockers}
            sectionName={sectionName}
            hardChecksOpen={outstandingHardChecksOwnLaag !== 0}
            reasonId={reviewSentenceId}
            {...(locked ? { blockedBy: lockReason } : {})}
            onHandover={async (event) => {
              await storage.writeNow()
              await onHandover?.(event)
            }}
          />
        </div>
      </div>
    )
  }

  return (
    <WaitScreen taal={language} gereed={ready}>
      <>
        <div className="ef-schilbalk" data-open={toolsOpen ? 'true' : 'false'}>
          {barStart === undefined ? null : <div className="ef-schilbalk__begin">{barStart}</div>}
          <div className="ef-schilbalk__status">
            {previewLabel !== undefined ? (
              <span className="ef-schilbalk__voorbeeld">{previewLabel}</span>
            ) : sessionExpired && storage.stand !== 'mislukt' ? (
              // Een mislukte opslag blijft die melding houden: die zegt meer dan "niet verbonden".
              <span className="ef-opslag" data-stand="niet-verbonden">
                {language === 'nl' ? 'Niet verbonden: log opnieuw in' : 'Not connected: sign in again'}
              </span>
            ) : (
              <StorageStatus
                stand={storage.stand}
                taal={language}
                {...(storage.laatstOpgeslagenOp === null ? {} : { opgeslagenOm: timeOf(storage.laatstOpgeslagenOp, language) })}
              />
            )}
            {awarenessSources.length === 0 || sessionExpired ? null : (
              <PresenceSummary sources={sectionPresence} taal={language} nameOf={presenceName} ik={identity} />
            )}
          </div>
          {onLanguage === undefined && logout === undefined && (awarenessSources.length === 0 || sessionExpired) ? null : (
            <>
              {/*
                Op een smal scherm staan taal en uitloggen achter één knop (ontwerp van het invulscherm,
                telefoon), zodat de kopbalk één regel blijft. Op een breed scherm verbergt de
                stylesheet deze knop en staan ze in de balk. Een disclosure en geen menu: wat erachter
                staat zijn gewone knoppen en een groep, geen menu-items. Delen en de panelen staan sinds
                2 november 2026 in de werkbalk van het formulier, ook op een telefoon (eisenboek 12.47).
              */}
              <button
                type="button"
                className="ef-schilbalk__meer"
                aria-expanded={toolsOpen}
                aria-controls={toolsId}
                onClick={() => setToolsOpen((open) => !open)}
              >
                <span aria-hidden="true">&#8943;</span>
                <span className="ef-sr-only">
                  {/* Wie er meewerkt staat op een telefoon ook achter deze knop (audit R-07). */}
                  {(() => {
                    const working = awarenessSources.length > 0 && !sessionExpired
                    const out = logout !== undefined
                    if (language === 'nl') return `Meer: ${['taal', ...(working ? ['wie er meewerkt'] : []), ...(out ? ['uitloggen'] : [])].join(', ').replace(/, ([^,]*)$/, ' en $1')}`
                    return `More: ${['language', ...(working ? ['who is working'] : []), ...(out ? ['sign out'] : [])].join(', ').replace(/, ([^,]*)$/, ' and $1')}`
                  })()}
                </span>
              </button>
              <div className="ef-schilbalk__gereedschap" id={toolsId}>
                {onLanguage === undefined ? null : <LanguageSwitch taal={language} onLanguage={onLanguage} compact />}
                {logout}
              </div>
            </>
          )}
        </div>
        <Form
          projectie={projection}
          mode={mode}
          {...(connections === undefined
            ? {}
            : { presencePer: (sectionId: string) => connections.get(sectionId)?.awareness })}
          presenceName={presenceName}
          presenceSelf={identity}
          outstandingPer={outstandingPer}
          hardChecksPer={tableOfContentsOpenPer}
          progressPer={progressPer}
          taal={language}
          visibility={visibility}
          renderField={renderField}
          sourceName={sourceNameOf}
          {...(werkverzameling === undefined ? {} : { werkverzameling })}
          {...(comments === undefined ? {} : { comments })}
          {...(changedFields === undefined ? {} : { changedFields })}
          {...(prefilledFields.size === 0 ? {} : { prefilledFields: [...prefilledFields] })}
          {...(beginSection === undefined ? {} : { beginSection })}
          {...(previousOutcomes === undefined ? {} : { previousOutcomes })}
          {...(earlierVersies === undefined ? {} : { earlierVersies })}
          {...(historyEarlier !== undefined
            ? { eerdereWaarden: historyEarlier, eerdereWaardenLabel: { nl: 'Toen', en: 'Then' } }
            : changedEarlierValues === undefined
              ? {}
              : { eerdereWaarden: changedEarlierValues })}
          {...(history === undefined ? {} : { historie: history })}
          {...(chosenVersie === undefined ? {} : { chosenVersie })}
          {...(onEarlierVersie === undefined ? {} : { onEarlierVersie })}
          {...(mode === 'nalezen' ? {} : { outstanding: { regels: outstandingRules, verzoek: request + look, fout: request > 0, indienen: sharedEntry === undefined, open: overviewOpen, id: overviewId, onSluiten: closeOverview } })}
          {...(accessNotice === undefined && (sharedEntry === undefined || bannerClosed || !fillMode) && !inHistory
            ? {}
            : {
                banner: (
                  <>
                    {chosenMoment === undefined || historyChoice === undefined ? null : (
                      <HistoryBanner
                        taal={language}
                        moment={chosenMoment.moment}
                        wijze={historyChoice.wijze}
                        legenda={historyLegend}
                        kapot={false}
                        bannerRef={historyBannerRef}
                        onTerug={leaveHistoryView}
                      />
                    )}
                    {accessNotice}
                    {sharedEntry === undefined || bannerClosed || !fillMode ? null : (
                      <SharedEntryBanner secties={projection.secties} deling={sharedEntry} taal={language} onSluit={closeBanner} />
                    )}
                  </>
                ),
              })}
          rechtZichtbaar={sharedEntry !== undefined && fillMode}
          {...(fillMode ? { alleenLezenPer: readOnlyMarker } : {})}
          contextHeader={contextHeader}
          renderFooter={footer}
          laagLijsten={layerLists}
          {...(reviewCriteria === undefined ? {} : { criteriaPer: reviewCriteria.per, criteriaProgress: reviewCriteria.progress })}
          {...(advice === undefined ? {} : { advies: advice })}
          {...(sidePanel === undefined ? {} : { zijpaneel: sidePanel })}
          {...(formPanels.panelen === undefined ? {} : { panelen: formPanels.panelen })}
        />
      </>
    </WaitScreen>
  )
}

/** Het contract en het pad van de naam van de call (`packages/model/registry/registry.json`, `call-publiceren`). */
const CALL_CONTRACT = 'call-publiceren'
const CALL_TITLE_PATH = 'call.titel'

/**
 * De naam van de call voor de contextregel van de kop, of niets (besluit van 2 november 2026).
 *
 * Alleen wat de pod echt heeft: de opgeloste verwijzing naar `call.titel` van het contract van de call,
 * een verplicht veld in dat contract. Draagt de definitie die verwijzing niet, of ziet deze lezer de
 * sectie niet waarin hij staat (R-63: dan is hij niet opgelost), of is hij nog niet beschikbaar, dan
 * staat er geen call; er wordt niets verzonnen en niets van buiten gehaald.
 */
export function callTitleOf(
  projection: Projection,
  verwijzingen: Readonly<Record<string, VerwijzingOutcome>> | undefined,
): string | undefined {
  if (verwijzingen === undefined) return undefined
  for (const section of projection.secties) {
    for (const field of section.velden) {
      if (field.control !== 'verwijzing' || field.bron.contract !== CALL_CONTRACT || field.bron.pad !== CALL_TITLE_PATH) continue
      const outcome = verwijzingen[field.id]
      if (outcome?.stand === 'waarde' && typeof outcome.waarde === 'string' && outcome.waarde.trim() !== '') return outcome.waarde.trim()
    }
  }
  return undefined
}

/** "rij 2", of "rijen 1 en 3", of "rijen 1, 2 en 4". */
function rowLabel(numbers: readonly number[], language: Language): string {
  const en = language === 'nl' ? 'en' : 'and'
  if (numbers.length === 0) return language === 'nl' ? 'een rij' : 'a row'
  if (numbers.length === 1) return `${language === 'nl' ? 'rij' : 'row'} ${String(numbers[0])}`
  const header = numbers.slice(0, -1).join(', ')
  return `${language === 'nl' ? 'rijen' : 'rows'} ${header} ${en} ${String(numbers[numbers.length - 1])}`
}

/** De stand van een rij criteria: geen, nog een zonder oordeel, of alle beoordeeld. */
function stateOf(verdicts: readonly ({ readonly voldoet: boolean } | undefined)[]): CriteriaState {
  if (verdicts.length === 0) return 'geen'
  if (verdicts.some((verdict) => verdict === undefined)) return 'open'
  return verdicts.every((verdict) => verdict?.voldoet === true) ? 'voldoet' : 'voldoet-niet'
}

/**
 * De neutrale zin over de sectie waar de invuller is (ontwerp van het invulscherm).
 *
 * Geen foutkleur en geen waarschuwing: een leeg verplicht veld is in een concept dat weken loopt
 * normaal. Wat er niet voldoet ondanks dat alles is ingevuld (een overschreden woordlimiet) zegt de
 * tweede vorm, zonder te zeggen wat: dat doen de melding bij het veld en het overzicht.
 */
function sectionSentence(empty: number | undefined, openChecks: number, openElsewhere: number, language: Language, submits = true): string {
  if (empty !== undefined && empty > 0) {
    return language === 'nl'
      ? `In deze sectie ${empty === 1 ? 'is nog 1 verplicht veld' : `zijn nog ${String(empty)} verplichte velden`} leeg.`
      : `${empty === 1 ? '1 required field is' : `${String(empty)} required fields are`} still empty in this section.`
  }
  if (openChecks > 0) {
    return language === 'nl'
      ? 'In deze sectie is alles ingevuld, maar nog niet alles klopt.'
      : 'Everything in this section is filled in, but not everything meets the conditions yet.'
  }
  if (openElsewhere > 0) {
    // Met het aantal, zoals het overzicht ("Nog 19 punten voordat u kunt indienen"), en niet "nog
    // iets": dat zei niet hoeveel (audit R-44).
    const nl = `Elders staan nog ${openElsewhere === 1 ? '1 punt' : `${String(openElsewhere)} punten`} open.`
    const en = `${openElsewhere === 1 ? '1 point is' : `${String(openElsewhere)} points are`} still open elsewhere.`
    return empty === undefined
      ? language === 'nl'
        ? `Hier is niets in te vullen. ${nl}`
        : `Nothing to fill in here. ${en}`
      : language === 'nl'
        ? `Deze sectie is klaar. ${nl}`
        : `This section is complete. ${en}`
  }
  if (!submits) return language === 'nl' ? 'Alles in uw secties is ingevuld.' : 'Everything in your sections is filled in.'
  return language === 'nl' ? 'Alles is ingevuld. U kunt indienen.' : 'Everything is filled in. You can submit.'
}

/**
 * De vaste helptekst in de voet, per gedaante (f1, R-157).
 *
 * Autosave is bij deze CRDT-opslag het dragende mechanisme en geen aparte handeling (ontwerp
 * 6.3), dus de eerste zin klopt in elke gedaante waarin geschreven wordt. Wat erna komt noemt de
 * handeling die in deze gedaante onderaan staat, en in `nalezen` is er geen.
 */
function helpText(mode: ViewMode, language: Language, unsaved = false): string {
  if (mode === 'nalezen') {
    return language === 'nl' ? 'Dit formulier is alleen te lezen.' : 'This form is read-only.'
  }
  // In een stand die niets opslaat (het voorbeeld, D-24) vervalt de zin over automatisch opslaan.
  const saved = unsaved ? '' : language === 'nl' ? 'Uw werk wordt automatisch opgeslagen. ' : 'Your work is saved automatically. '
  return language === 'nl'
    ? `${saved}Indienen kan als alles is ingevuld.`
    : `${saved}Submitting is possible once every condition is met.`
}

/** Waarom indienen en de toetsknoppen uit staan zolang er een stand uit de geschiedenis staat. */
const HISTORY_REASON = {
  nl: 'U bekijkt een eerdere stand. Ga eerst terug naar nu; daarna kunt u verder.',
  en: 'You are viewing an earlier version. Go back to now first; then you can continue.',
} as const

/**
 * Of het formulier nu achter een paneel staat: op een telefoon het blad, op een smal scherm de tweede
 * weergave. Dan moet het paneel na het kiezen van een stand dicht, anders ziet de lezer niet wat hij koos.
 */
function formOutOfView(): boolean {
  if (document.querySelector('dialog.ef-paneelblad[open]') !== null) return true
  const column = document.querySelector<HTMLElement>('.ef-formulier__kolom')
  return column !== null && column.offsetParent === null
}

/** Elke sectie alleen te lezen, voor de weergave van een stand uit de geschiedenis. */
function readOnlyProjection(projection: Projection): Projection {
  return { ...projection, secties: projection.secties.map((section) => (section.recht === 'lezen' ? section : { ...section, recht: 'lezen' })) }
}

/** Waarom indienen en de toetsknoppen uit staan na een verlopen sessie. */
const SESSION_REASON = {
  nl: 'Log eerst opnieuw in; daarna kunt u verder.',
  en: 'Sign in again first; then you can continue.',
} as const

/** De melding bij een criterium zonder oordeel, in het overzicht van wat er nog openstaat. */
const NO_VERDICT = { nl: 'Nog geen oordeel.', en: 'No judgement yet.' } as const

/** Eén zin die zegt welke criteria nog een oordeel missen (R-140). */
function sentenceWithoutVerdict(names: readonly string[], language: Language): string {
  return language === 'nl'
    ? `Indienen kan pas als elk criterium een oordeel heeft; nog zonder oordeel: ${names.join(', ')}.`
    : `Submitting is only possible once every criterion has a judgement; still without one: ${names.join(', ')}.`
}

/**
 * De inhoud van een antwoord, zonder wie en wanneer, om twee standen te vergelijken (R-96). Een
 * kale waarde telt als die waarde, en alles wat leeg is (geen antwoord, lege tekst, geen keuze)
 * telt als hetzelfde leeg.
 */
function contentOf(answer: unknown): string {
  const shaped = answer !== null && typeof answer === 'object' ? answer : answer === undefined ? undefined : { waarde: answer }
  if (shaped === undefined || isEmpty(shaped as Parameters<typeof isEmpty>[0])) return 'leeg'
  const { door: _by, gewijzigdOp: _on, ...content } = shaped as Record<string, unknown>
  return JSON.stringify(content)
}

/** Het label van de knop naar het overzicht van wat er openstaat, in de kop en in de voet van de toetser. */
const SHOW_OUTSTANDING: Readonly<Record<'nl' | 'en', string>> = { nl: 'Toon wat er openstaat', en: 'Show what is open' }
const HIDE_OUTSTANDING: Readonly<Record<'nl' | 'en', string>> = { nl: 'Verberg wat er openstaat', en: 'Hide what is open' }
